import {
  faFileUpload,
  faLocationCrosshairs,
  faTruck,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ExcelJS from "exceljs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Check,
  Download,
  Info,
  Paperclip,
  Search,
  X,
} from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Radio } from "semantic-ui-react";
import { getTemplates } from "../../api/finance/addChargesApi";
import {
  enableFoc,
  flagLogHistory,
  jobConfirmation,
  jobFreezeAndUnFreeze,
  maxCbmByJobId,
  retryChargeableKM,
  retryKmActual,
  selectKmMethod,
  shipmentStatus,
  shipmentStatusRemainingData,
  updateJob,
  userBasedJobSearch,
  vehicleAssignmentHistory,
} from "../../api/jobApi";
import { getBulkDropdownData } from "../../api/keyvalue";
import { statusIdsByContexts } from "../../api/statusApi";
import { getSystemConfig } from "../../api/systemConfigApi";
import { getFieldAuthority, getFormAuthority } from "../../auth/authHelper";
import Grid from "../../components/Grid";
import LoadingSpinner from "../../components/LoadingSpinner";
import Modal from "../../components/Modal";
import Tab from "../../components/Tab";
import AlertConfirmation from "../../components/input/AlertConfirmation";
import Button from "../../components/input/Button";
import Dropdown from "../../components/input/Dropdown";
import SideDrawer from "../../components/input/SideDrawer";
import ButtonRenderer from "../../components/renderer/ButtonRenderer";
import IconButtonRenderer from "../../components/renderer/IconButtonRenderer";
import SelectRenderer from "../../components/renderer/SelectRenderer";
import {
  RemarkFlagRenderer,
  ResizableScrollingTextRenderer,
  StatusLabelStyle,
  VehicleAssignmentStyle,
} from "../../components/renderer/StyleRenderer";
import FuelQuota from "../../components/transaction/JobMonitor/FuelQuota/FuelQuota";
import JobConfirmation from "../../components/transaction/JobMonitor/JobConfirmation";
import JobDisputeResolution from "../../components/transaction/JobMonitor/JobDisputeResolution";
import JobKmMethodSelection from "../../components/transaction/JobMonitor/JobKmMethodSelection";
import JobMonitorAdvancedSearch from "../../components/transaction/JobMonitor/JobMonitorAdvancedSearch";
import JobMonitorFlag from "../../components/transaction/JobMonitor/JobMonitorFlag";
import JobMonitorNegotiation from "../../components/transaction/JobMonitor/JobMonitorNegotiation";
import JobMonitorReportComponent from "../../components/transaction/JobMonitor/JobMonitorReportComponent";
import JobMonitorSearch from "../../components/transaction/JobMonitor/JobMonitorSearch";
import JobUpload from "../../components/transaction/JobMonitor/JobUpload";
import RequestCopyModal from "../../components/transaction/JobMonitor/RequestCopyModal";
import JobStatusManagement from "../../components/transaction/JobMonitor/StatusHistory/JobStatusManagement";
import TimeUpdate from "../../components/transaction/JobMonitor/TimeUpdate/TimeUpdate";
import VehicleAssignment from "../../components/transaction/JobMonitor/VehicleAssignment/VehicleAssignment";
import JobVehicleStatus from "../../components/transaction/JobMonitor/VehicleStatus/JobVehicleStatus";
import RequestCreationRefactor from "../../components/transaction/RequestCreation/RequestCreationRefactor";
import {
  AUTH_TYPES,
  AUTHORITY_TYPE,
  chargePanelColumnList,
  FORMS,
  gridHeaders,
  jobMonitorColumnList,
  locationSocketUrl,
  MODULES,
  PINNED_COLUMN_COLOR,
  STATUS_GROUPS,
  SYSTEM_CONFIGS,
  timeUpdateColumnList,
} from "../../const";
import {
  formatCsvDate,
  formatFromJsonDate,
  oppFormatFromJsonDate,
  oppToExcelDateTime,
} from "../../dateConfig";
import useUserCompany from "../../hooks/useUserCompany";
import WebSocketConnection from "../../service/WebSocketConnection";
import { reportActions } from "../../store/report/report-slice";
import JobMonitorEvInvCreation from "../finance/JobMonitorEvInvCreation";
import JobReport from "./JobReport";
import { FaStar } from "react-icons/fa";
import JobTrackLink from "../../components/transaction/JobMonitor/JobTrackLink";

const form = FORMS.JOB.MONITOR.form;

const mode = {
  EV: 3,
  INV: 4,
};

const template = {
  JOB_MONITOR_TEMPLATE: 1,
  TIME_UPDATE_TEMPLATE: 2,
  CHARGE_PANEL_TEMPLATE: 3,
  NO_TEMPLATE: 4,
};

const STORAGE_KEY = "jobMonitor.selectedHeaders";
const ACTIVE_TEMPLATE = "jobMonitor.activeTemplate";

function JobMonitor() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const jobConfirmationAlertSearchParams = {
    id: searchParams.get("id"),
    type: searchParams.get("type"),
  };
  const columnConfig = useSelector((state) => state.dynamicColumn.columnConfig);
  const auth = useSelector(
    (state) => state.user.permissions[MODULES.Transaction],
  );

  const userData = useSelector((state) => state.user.userDetails);
  const user = useSelector((state) => state.user);
  const focRef = useRef();
  const userCompany = useUserCompany();
  const userCurrency = userCompany ? userCompany.comCurrency : null;
  const userCurrencyCode = userCompany ? userCompany.comCurrencyCode : null;

  const jobMonitorPermission = auth[FORMS?.JOB?.MONITOR?.form];
  console.log("Job Monitor Permission", jobMonitorPermission);
  const shipmentStatusReportPermission =
    jobMonitorPermission?.FORM?.SHIPMENT_STATUS_REPORT;
  const flagLogPermission = jobMonitorPermission?.FORM?.FLAG_LOG_REPORT;
  const vehicleLogPermission =
    jobMonitorPermission?.FORM?.VEHICLE_ASSIGNMENT_LOG_REPORT;
  const autoDistancePermission =
    jobMonitorPermission?.AUTO_DISTANCE_CALCULATE?.VIEW;
  const ignoreDocFreezePermission =
    jobMonitorPermission?.FORM?.IGNORE_DOC_UPLOAD_FREEZE;
  const requestCopyPermission = jobMonitorPermission?.FORM?.REQUEST_COPY;

  const freezeDocUploadPermission =
    jobMonitorPermission?.FORM?.FREEZE_DOC_UPLOAD;
  const freezeTimeUpdatePermission =
    jobMonitorPermission?.FORM?.FREEZE_TIME_UPDATE;
  const unFreezeDocUploadPermission =
    jobMonitorPermission?.FORM?.UNFREEZE_DOC_UPLOAD;
  const unFreezeTimeUpdatePermission =
    jobMonitorPermission?.FORM?.UNFREEZE_TIME_UPDATE;

  const focRevPermission = jobMonitorPermission?.FORM?.FOC_REV;
  console.log("focRevPermission", focRevPermission);
  const focCostPermission = jobMonitorPermission?.FORM?.FOC_COST;
  console.log("focCostPermission", focCostPermission);

  const jobMonitorTemplatePermission =
    jobMonitorPermission?.FORM?.JOB_MONITOR_TEMPLATE;
  const timeUpdateTemplatePermission =
    jobMonitorPermission?.FORM?.TIME_UPDATE_TEMPLATE;
  const chargePanelTemplatePermission =
    jobMonitorPermission?.FORM?.CHARGE_PANEL_TEMPLATE;

  const jobReportPermission = jobMonitorPermission?.FORM?.JOB_REPORT;

  const kmDeviationUpdatePermission =
    auth?.[FORMS.JOB.KM_DEVIATION_UPDATE.form]?.FORM?.CREATE;

  const liveTrackOptionPermission = auth?.[FORMS.JOB.LIVE_TRACK_OPTION.form]?.FORM?.VIEW;

  const [tab, setTab] = useState(1);
  const [activeAccordion, setActiveAccordion] = useState(1);
  const [selectedRows, setSelectedRows] = useState(null);
  const [popUpContent, setPopUpContent] = useState(null);
  const [drawerContent, setDrawerContent] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchResultsPending, setSearchResultsPending] = useState(false);
  const [arrivalConfirmationOpt, setArrivalConfirmationOpt] = useState([]);
  const [yesNoOptions, setYesNoOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [currentJobCode, setCurrentJobCode] = useState(null);
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const [timeFreeze, setTimeFreeze] = useState(false);
  const [currentRequestCode, setCurrentRequestCode] = useState(null);
  const [updateState, setUpdateState] = useState({});
  const [alertOpen, setAlertOpen] = useState(false);
  const [dropdownUpdateLoading, setDropdownUpdateLoading] = useState(false);
  const [currentTransporter, setCurrentTransporter] = useState(null);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [currentVehicleType, setCurrentVehicleType] = useState(null);
  const [showCloseButton, setShowCloseButton] = useState(false);
  const [currentSearchData, setCurrentSearchData] = useState({});
  const [updateNeeded, setUpdateNeeded] = useState(false);
  const [currentVehicle, setCurrentVehicle] = useState(null);
  const [currentDriver, setCurrentDriver] = useState(null);
  const [timeUpdateViewMode, setTimeUpdateViewMode] = useState(true);
  const [requestViewMode, setRequestViewMode] = useState(true);
  const [jobInfo, setJobInfo] = useState({});
  const [currentStaId, setCurrentStaId] = useState(null);
  const [currentVehicleTypeId, setCurrentVehicleTypeId] = useState(null);
  const [currentContainerTypeId, setCurrentContainerTypeId] = useState(null);
  const [trackingEnabled, setTrakingEnabled] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [actionMenuOptions, setActionMenuOptions] = useState([]);
  const [firstArrivalTime, setFirstArrivalTime] = useState(null);
  const [lastDepartureTime, setLastDepartureTime] = useState(null);
  const [bookingRequiredDate, setBookingRequiredDate] = useState(null);
  const [kmCalculationEnabledStatus, setKmCalculationEnabledStatus] = useState(
    [],
  );
  const [arrivalConfEnStatus, setArrivalConfEnStatus] = useState([]);
  const [routeConfEnStatus, setRouteConfEnStatus] = useState([]);
  const [kmDeviationEnStatus, setKmDeviationEnStatus] = useState([]);
  const [requestEditModeEnabledListIds, setRequestEditModeEnabledIds] =
    useState([]);
  const [tueList, setTueList] = useState([]);
  const [operationCompleteStatus, setOperationCompleteStatus] = useState(null);
  const operationCompleteStatusRef = useRef();
  const kmCalcActionIdsRef = useRef([]);
  const adHocEvInvEnableListRef = useRef();
  const releaseNoteEnableListRef = useRef();
  const focEnabledListRef = useRef();
  const locationBasedReleaseNoteEnableListRef = useRef();
  const [jobCompleteStatus, setJobCompleteStatus] = useState(null);
  const [jobDisputeStatus, setJobDisputeStatus] = useState(null);
  const [kmMethodViewOnly, setKmModeViewOnly] = useState(false);
  const [kmActualAmount, setKmActualAmount] = useState(null);
  const [jobStatusList, setJobStatusList] = useState([]);
  const wsConnection = useMemo(
    () => new WebSocketConnection(locationSocketUrl, {}),
    [],
  );
  const [templates, setTemplates] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState(3);
  const [evInvMode, setEvInvMode] = useState("");
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState(() => {
    const stored = localStorage.getItem(ACTIVE_TEMPLATE);
    return stored
      ? Number(stored)
      : jobMonitorTemplatePermission
        ? template.JOB_MONITOR_TEMPLATE
        : timeUpdateTemplatePermission
          ? template.TIME_UPDATE_TEMPLATE
          : template.CHARGE_PANEL_TEMPLATE;
  });
  // const [selectedHeaders, setSelectedHeaders] = useState({});
  const [jobEditDisabledList, setJobEditDisabledList] = useState([]);
  const [defaultFilter, setDefaultFilter] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSsrDownloading, setIsSsrDownloading] = useState(false);
  const [kmAdded, setKmAdded] = useState([]);
  const [jCbmEditableList, setJCbmEditableList] = useState([]);
  const [poNoEditableList, setPoNoEditableList] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [popUpKey, setPopUpKey] = useState(1);
  const [releaseNoteVariant, setReleaseNoteVariant] = useState("standard");
  const dispatch = useDispatch();
  const [gridConfig, setGridConfig] = useState({});
  const [currentJobResolvedStatus, setCurrentJobResolvedStatus] =
    useState(null);
  const [
    shipmentStatusInvalidateDuration,
    setShipmentStatusInvalidateDuration,
  ] = useState("");
  const [searchedDateTime, setSearchedDateTime] = useState("");
  const [searchClicked, setSearchClicked] = useState(false);
  const gridRef = useRef();
  const [isFreezing, setIsFreezing] = useState(false);
  const [isUnFreezing, setIsUnFreezing] = useState(false);
  const [focType, setFocType] = useState(null);
  const [selectedHeaders, setSelectedHeaders] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored
      ? JSON.parse(stored)
      : jobMonitorTemplatePermission
        ? jobMonitorColumnList
        : timeUpdateTemplatePermission
          ? timeUpdateColumnList
          : chargePanelColumnList;
  });

  const eventKeys = {
    KM_ACTUAL: "jobKmActual",
    ARRIVAL_CONFIRMATION: "jobArrivalConfirmation",
    KM_DEVIATION: "kmDeviation",
    DOC_APPROVED: "jobDocApproved",
    ROUTE_CONFIRMATION: "jobRouteConfirm",
    ARRIVAL_ON_TIME: "jobOnTime",
    JOB_AOD: "jobAod",
    Job_Call_Center_Remark: "jobCallCenterRemark",
    JOB_OTD_REMARK: "jobOtdRemark",
    JOB_SEAL1: "jobSeal1",
    JOB_SEAL2: "jobSeal2",
    JOB_SEAL3: "jobSeal3",
    JOB_MOUX: "jobMoux",
    JOB_CARTON: "jobCartoon",
    STATUS: "jobCurrentStatus",
    STATUS_VALUE: "gStaName",
    VEHICLE: "gJobVehicle",
    FLAG: "gLatestFlag",
    REMARKS: "gLatestRemark",
    LOCATION: "gLocationStatus",
    REQUEST: "gReqCode",
    JOB: "gJobCode",
    DED_FUEL: "",
    JOB_REF: "jobReference",
    JOB_VEH_ASSIGN_REMARK: "jobVehAssignRemark",
    JOB_OPERATION_REMARK: "jobOperationRemark",
    JOB_CUSTOMER_FEEDBACK: "jobCustomerFeedback",
    JOB_HELDUP_REMARK: "jobHeldUpRemark",
    JOB_CBM: "gJobCbm",
    REQ_PO_NO: "reqPoNo",
    JOB_ADDITIONAL_NO: "jobAdditionalNo",
  };

  useEffect(() => {
    (async () => {
      const invalidateDurationStr = await getSystemConfig(
        SYSTEM_CONFIGS.SHIPMENT_STATUS_INVALIDATION_DURATION,
        "10",
      );
      const minutes = Number(invalidateDurationStr);
      setShipmentStatusInvalidateDuration(
        Number.isFinite(minutes) ? minutes : 10,
      );
    })();
  }, []);

  const isExpired = useMemo(() => {
    if (!searchedDateTime || shipmentStatusInvalidateDuration == null)
      return true;

    const expiryMs =
      searchedDateTime.getTime() + shipmentStatusInvalidateDuration * 60 * 1000; // minutes -> ms

    return Date.now() > expiryMs;
  }, [searchedDateTime, shipmentStatusInvalidateDuration]);

  function convertSecondsToHMS(totalSeconds) {
    // Ensure totalSeconds is an integer (round down)
    const total = Math.floor(totalSeconds);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;

    const parts = [];
    if (hours > 0) {
      parts.push(`${hours} ${hours === 1 ? "Hr" : "Hrs"}`);
    }
    if (minutes > 0) {
      parts.push(`${minutes} ${minutes === 1 ? "Min" : "Mins"}`);
    }
    // Always show seconds if nothing else is present
    if (seconds > 0 || parts.length === 0) {
      parts.push(`${seconds} ${seconds === 1 ? "Sec" : "Secs"}`);
    }

    return parts.join(", ");
  }

  const handleDisputeResolutionSuccess = (jobId) => {
    setSearchResults((prevResults) =>
      prevResults.map((job) =>
        job.jobId === jobId
          ? {
              ...job,
              disputed: false,
              jobDisputeResolved: 1,
              confStatus: 2,
            }
          : job,
      ),
    );

    closePopup(false);

    toast.success("Dispute resolved. You can now confirm the job.");
  };

  const allGridConfig = [
    {
      headerName: "Request Id",
      id: "gReqCode",
      field: "gReqCode",
      authenticated: true,
      module: MODULES.Transaction,
      primaryForm: form,
      secondaryForm: FORMS.REQUEST.CREATION.form,
      headerCheckboxSelection: true,
      checkboxSelection: true,
      // pinned: "left",
      cellRenderer: (params) => {
        return (
          <div
            style={{
              color: params.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "5px",
              position: "relative",
            }}
          >
            {params.hasRemark && (
              <div
                style={{
                  position: "absolute",
                  top: "1px",
                  left: "-8px",
                  width: "6px",
                  height: "6px",
                  backgroundColor: "red",
                  borderRadius: "50%",
                  zIndex: 10,
                }}
              />
            )}
            <span>{params.value}</span>

            {params.isBackhaul && (
              <div
                title={`Backhaul: ${params.backhaulCode || "Yes"}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: "#f39c12",
                }}
              >
                <FontAwesomeIcon icon={faTruck} />
              </div>
            )}
            {params.hasDocs && (
              <div className="header-attachment-icon">
                <Paperclip size={17} className="attachment" />
                <label className="attachment-count">
                  {params.docCount > 100 ? "99+" : params.docCount}
                </label>
              </div>
            )}
          </div>
        );
      },
      // cellStyle: (params) => {
      //   return params.data?.reqLateBooking === "yes" ||
      //     params.data?.reqLateBooking === "Yes"
      //     ? { backgroundColor: "#ffcccb" }
      //     : { backgroundColor: "#90ee90" };
      // },
      cellRendererParams: (params) => ({
        // color:
        //   params.data?.gJobVehicle === "Vehicle not assigned"
        //     ? "#3498db"
        //     : "green",
        // color:
        //   params.data?.jobArrStatus === "Ontime" ||
        //   params.data?.jobArrStatus === "Early"
        //     ? "#279616ff"
        //     : "",
        color:
          params.data?.reqLateBooking === "No" ||
          params.data?.reqLateBooking === "no"
            ? "#279616ff"
            : "",
        docCount: params.data?.reqDocCount,
        hasDocs: params.data?.hasReqDocs,
        hasRemark: params.data?.hasCreatorRemark,
        isBackhaul: params.data?.reqIsBackhaul,
        backhaulCode: params.data?.backhaulRequestCode,
      }),
      filter: true,
    },
    {
      headerName: "Job No",
      id: "gJobCode",
      module: MODULES.Transaction,
      primaryForm: form,
      secondaryForm: FORMS.REQUEST.CREATION.form,
      authenticated: true,
      filter: true,
      cellRenderer: (params) => {
        return (
          <div
            style={{
              color: params.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "5px",
            }}
          >
            <span>{params.value}</span>
            {params.hasDocs && (
              <div className="header-attachment-icon">
                <Paperclip size={17} className="attachment" />
                <label className="attachment-count">
                  {params.docCount > 100 ? "99+" : params.docCount}
                </label>
              </div>
            )}
          </div>
        );
      },
      cellRendererParams: (params) => ({
        color: params.data?.jobArrStatus === "Delay" ? "#ea777dff" : "",
        docCount: params.data?.docCount,
        hasDocs: params.data?.hasDocs,
      }),
      field: "gJobCode",
    },
    {
      headerName: "Creator Remark",
      module: MODULES.Transaction,
      authenticated: true,
      cellRenderer: ResizableScrollingTextRenderer,
      primaryForm: form,
      field: "creatorRemark",
      filter: true,
      id: "creatorRemark",
    },
    {
      headerName: "Reference",
      field: eventKeys.JOB_REF,
      id: "reference",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      editable: (param) => !jobEditDisabledList.includes(param.data?.staId),
      cellStyle: (param) => ({
        backgroundColor: !jobEditDisabledList.includes(param.data?.staId)
          ? "#fff9c4"
          : "#ffffff",
      }),
    },
    {
      headerName: "Customer",
      field: "gReqCustomer",
      id: "gReqCustomer",
      module: MODULES.Transaction,
      primaryForm: form,
      cellRenderer: ResizableScrollingTextRenderer,
      authenticated: true,
      filter: true,
    },
    {
      headerName: "Billing Customer",
      field: "gReqBillingCustomer",
      id: "gReqBillingCustomer",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
    },
    {
      headerName: "Invoicing Customer",
      field: "gReqInvoicingCustomer",
      id: "gReqInvoicingCustomer",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
    },
    {
      headerName: "Requesting Plant",
      field: "gReqPlant",
      id: "gReqPlant",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
    },
    {
      headerName: "Transporter",
      field: "gJobTransporter",
      id: "gJobTransporter",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
    },
    {
      headerName: "Transporter Contact",
      field: "conMobile",
      id: "conMobile",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      cellDataType: "text",
    },
    {
      headerName: "OTD Cutoff Time",
      field: "reqDeliveryCutOffTime",
      id: "reqDeliveryCutOffTime",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      type: "dateCol",
    },
    {
      headerName: "Contact Person",
      field: "gContactPerson",
      id: "gContactPerson",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
    },
    {
      headerName: "Request Invoice Reference",
      field: "reqInvoiceRef",
      id: "reqInvoiceRef",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
    },
    {
      headerName: "Start Location",
      field: "startLocation",
      id: "startLocation",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      cellStyle: (param) => ({
        backgroundColor: "#8fb4daff",
      }),
    },
    {
      headerName: "Requested Arrival Time",
      field: "arrivalEta",
      id: "arrivalEta",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      cellStyle: (param) => ({
        backgroundColor: "#8fb4daff",
      }),
      type: "dateCol",
    },
    {
      headerName: "End Location",
      field: "endLocation",
      id: "endLocation",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      cellStyle: (param) => ({
        backgroundColor: "#8fb4daff",
      }),
    },
    {
      headerName: "Requested Delivery Time",
      field: "deliveryEta",
      id: "deliveryEta",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      cellStyle: (param) => ({
        backgroundColor: "#8fb4daff",
      }),
      type: "dateCol",
    },
    {
      headerName: "Late Accept",
      field: "reqLateAccept",
      id: "reqLateAccept",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellStyle: (params) => {
        return params.data?.reqLateAccept === "Yes" ||
          params.data?.reqLateAccept === "yes"
          ? { backgroundColor: "#ffcccb" }
          : { backgroundColor: "#90ee90" };
      },
      width: "150",
      minWidth: "150",
    },
    {
      headerName: "Late Booking",
      field: "reqLateBooking",
      id: "reqLateBooking",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellStyle: (params) => {
        return params.data?.reqLateBooking === "yes" ||
          params.data?.reqLateBooking === "Yes"
          ? { backgroundColor: "#ffcccb" }
          : { backgroundColor: "#90ee90" };
      },
      width: "150",
      minWidth: "150",
    },
    {
      headerName: "Date Created",
      field: "gCreatedDate",
      id: "gCreatedDate",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      minWidth: 250,
      type: "dateCol",
    },
    {
      headerName: "Shipment Creator",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "gCreatedBy",
      id: "gCreatedBy",
    },
    {
      headerName: "Job Created By",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "jobCreatedBy",
      id: "jobCreatedBy",
    },
    {
      headerName: "Job Created Date",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "jobCreatedDate",
      id: "jobCreatedDate",
      type: "dateCol",
    },
    {
      headerName: "Behalf Of",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "behalfOfUser",
      id: "behalfOfUser",
    },
    {
      headerName: "Shipment Type",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "shipmentType",
      id: "shipmentType",
    },
    {
      headerName: "Consignment Type",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "consignmentType",
      id: "consignmentType",
    },
    {
      headerName: "Consignee",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "consignee",
      id: "consignee",
    },
    {
      headerName: "KAG Number",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "reqKagNoValue",
      id: "reqKagNoValue",
      cellRenderer: ResizableScrollingTextRenderer,
    },
    {
      headerName: "Other Requests",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      field: "otherCharges",
      id: "otherCharges",
    },
    {
      headerName: "Vehicle Type",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "gVhtType",
      id: "gVhtType",
    },
    {
      headerName: "Driver Name",
      field: "gDriverName",
      id: "gDriverName",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      cellRendererParams: (params) => ({
        value: params.value,
        isBirthday: params.data?.isDriverBirthdayToday,
      }),
      cellStyle: (params) =>
        params.data?.isDriverBirthdayToday
          ? {
              backgroundColor: "#fff176",
            }
          : {},
    },
    {
      headerName: "Driver License",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "gDrvLicenceNo",
      id: "gDrvLicenceNo",
    },
    {
      headerName: "Driver Phone",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "gDrvPhoneNo",
      id: "gDrvPhoneNo",
    },
    {
      headerName: "Driver NIC",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "driverNic",
      id: "driverNic",
    },
    {
      headerName: "Helper Name",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "gHelperName",
      id: "gHelperName",
    },
    {
      headerName: "Helper NIC",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "gHelNicNo",
      id: "gHelNicNo",
    },
    {
      headerName: "Helper Phone",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "gHelPhoneNo",
      id: "gHelPhoneNo",
    },
    {
      headerName: "Shipment Approval Time",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "gReqBookingApprovalDate",
      id: "gReqBookingApprovalDate",
      minWidth: 250,
      type: "dateCol",
    },
    {
      headerName: "Approved By",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "gReqBookingApprovedBy",
      id: "gReqBookingApprovedBy",
    },
    {
      headerName: "Req PO No",
      field: eventKeys.REQ_PO_NO,
      id: "reqPoNo",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      // field: "reqPoNo",
      editable: (param) => poNoEditableList?.includes(param.data?.staId),
      cellStyle: (param) => ({
        backgroundColor: poNoEditableList.includes(param.data?.staId)
          ? "#fff9c4"
          : "#ffffff",
      }),
    },
    {
      headerName: "Job Aod",
      field: eventKeys.JOB_AOD,
      id: "jobAod",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      editable: (param) => !jobEditDisabledList.includes(param.data?.staId),
      cellStyle: (param) => ({
        backgroundColor: !jobEditDisabledList.includes(param.data?.staId)
          ? "#fff9c4"
          : "#ffffff",
      }),
      cellRenderer: ResizableScrollingTextRenderer,
    },
    {
      headerName: "Monitoring Center Remark",
      id: "jobCallCenterRemark",
      field: eventKeys.Job_Call_Center_Remark,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      editable: (param) => !jobEditDisabledList.includes(param.data?.staId),
      cellStyle: (param) => ({
        backgroundColor: !jobEditDisabledList.includes(param.data?.staId)
          ? "#fff9c4"
          : "#ffffff",
      }),
    },
    {
      headerName: "OTD Remark",
      id: "jobOtdRemark",
      field: eventKeys.JOB_OTD_REMARK,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      editable: (param) => !jobEditDisabledList.includes(param.data?.staId),
      cellStyle: (param) => ({
        backgroundColor: !jobEditDisabledList.includes(param.data?.staId)
          ? "#fff9c4"
          : "#ffffff",
      }),
    },
    {
      headerName: "Additional Mobile No",
      id: "jobAdditionalNo",
      field: eventKeys.JOB_ADDITIONAL_NO,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      valueParser: (params) => params.newValue,
      editable: (param) => !jobEditDisabledList.includes(param.data?.staId),
      cellStyle: (param) => ({
        backgroundColor: !jobEditDisabledList.includes(param.data?.staId)
          ? "#fff9c4"
          : "#ffffff",
      }),
    },
    {
      headerName: "Job Seal 1",
      id: "jobSeal1",
      field: eventKeys.JOB_SEAL1,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      editable: (param) => !jobEditDisabledList.includes(param.data?.staId),
      cellStyle: (param) => ({
        backgroundColor: !jobEditDisabledList.includes(param.data?.staId)
          ? "#fff9c4"
          : "#ffffff",
      }),
    },
    {
      headerName: "Job Seal 2",
      id: "jobSeal2",
      field: eventKeys.JOB_SEAL2,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      editable: (param) => !jobEditDisabledList.includes(param.data?.staId),
      cellStyle: (param) => ({
        backgroundColor: !jobEditDisabledList.includes(param.data?.staId)
          ? "#fff9c4"
          : "#ffffff",
      }),
    },
    {
      headerName: "Job Seal 3",
      id: "jobSeal3",
      field: eventKeys.JOB_SEAL3,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      editable: (param) => !jobEditDisabledList.includes(param.data?.staId),
      cellStyle: (param) => ({
        backgroundColor: !jobEditDisabledList.includes(param.data?.staId)
          ? "#fff9c4"
          : "#ffffff",
      }),
    },
    {
      headerName: "Job Moux",
      field: eventKeys.JOB_MOUX,
      id: "jobMoux",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      editable: (param) => !jobEditDisabledList.includes(param.data?.staId),
      cellStyle: (param) => ({
        backgroundColor: !jobEditDisabledList.includes(param.data?.staId)
          ? "#fff9c4"
          : "#ffffff",
      }),
    },
    {
      headerName: "Job Carton",
      field: eventKeys.JOB_CARTON,
      id: "jobCarton",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellEditor: "agNumberCellEditor",
      valueParser: (params) => {
        return params.newValue === "" ? null : Number(params.newValue);
      },
      editable: (param) => !jobEditDisabledList.includes(param.data?.staId),
      cellStyle: (param) => ({
        backgroundColor: !jobEditDisabledList.includes(param.data?.staId)
          ? "#fff9c4"
          : "#ffffff",
      }),
    },
    {
      headerName: "Job Veh Assign Remark",
      field: eventKeys.JOB_VEH_ASSIGN_REMARK, //
      id: "jobVehAssignRemark",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      editable: (param) => !jobEditDisabledList.includes(param.data?.staId),
      cellStyle: (param) => ({
        backgroundColor: !jobEditDisabledList.includes(param.data?.staId)
          ? "#fff9c4"
          : "#ffffff",
      }),
    },
    {
      headerName: "Job Operation Remark",
      field: eventKeys.JOB_OPERATION_REMARK, //
      id: "jobOperationRemark",
      module: MODULES.Transaction,
      authenticated: true,
      filter: true,
      primaryForm: form,
      editable: (param) => !jobEditDisabledList.includes(param.data?.staId),
      cellStyle: (param) => ({
        backgroundColor: !jobEditDisabledList.includes(param.data?.staId)
          ? "#fff9c4"
          : "#ffffff",
      }),
    },
    {
      headerName: "Job Customer Feedback",
      id: "jobCustomerFeedback",
      field: eventKeys.JOB_CUSTOMER_FEEDBACK,
      module: MODULES.Transaction,
      authenticated: true,
      filter: true,
      primaryForm: form,
      editable: (param) => !jobEditDisabledList.includes(param.data?.staId),
      cellStyle: (param) => ({
        backgroundColor: !jobEditDisabledList.includes(param.data?.staId)
          ? "#fff9c4"
          : "#ffffff",
      }),
    },
    {
      headerName: "Job Held Up Remark",
      id: "jobHeldUpRemark",
      field: eventKeys.JOB_HELDUP_REMARK,
      module: MODULES.Transaction,
      authenticated: true,
      filter: true,
      primaryForm: form,
      editable: (param) => !jobEditDisabledList.includes(param.data?.staId),
      cellStyle: (param) => ({
        backgroundColor: !jobEditDisabledList.includes(param.data?.staId)
          ? "#fff9c4"
          : "#ffffff",
      }),
    },
    {
      minWidth: 250,
      module: MODULES.Transaction,
      primaryForm: form,
      field: "supplierNegotiation",
      authenticated: true,
      cellRenderer: ButtonRenderer,
      cellRendererParams: (params) => ({
        clickHandler: () => {
          handleGridButtonClick(params.data, gridHeaders.SUPPLER_NEGO);
        },
        style: { width: "215px" },
        className: params.data?.hasSupNego
          ? "ui grey button"
          : "ui primary button",
        disabled: !params.data?.jobTransporterId,
        label: params.data?.jobTransporterId
          ? params.data?.hasSupNego
            ? `Supplier Negotiations (${
                params.data?.supNegoCount > 100
                  ? "99+"
                  : params.data?.supNegoCount
              })`
            : "Supplier Negotiation"
          : "No Supplier Available",
        authenticated: true,
        form: `${FORMS.JOB.NEGOTIATION.form}_4`,
        module: MODULES.Transaction,
        editMode: false,
      }),
      id: "supplierNego",
      headerName: gridHeaders.SUPPLER_NEGO,
    },
    {
      minWidth: 250,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      field: "customerNegotiation",
      cellRenderer: ButtonRenderer,
      cellRendererParams: (params) => ({
        clickHandler: () => {
          handleGridButtonClick(params.data, gridHeaders.CUSTOMER_NEGO);
        },
        className: params.data?.hasCusNego
          ? "ui red button"
          : "ui primary button",
        label: params.data?.hasCusNego
          ? `Customer Negotiations (${
              params.data?.cusNegoCount > 100
                ? "99+"
                : params.data?.cusNegoCount
            })`
          : "Customer Negotiation",
        style: { width: "215px" },
        authenticated: true,
        form: `${FORMS.JOB.NEGOTIATION.form}_1`,
        module: MODULES.Transaction,
        editMode: false,
      }),
      headerName: gridHeaders.CUSTOMER_NEGO,
      id: "customerNego",
    },
    {
      headerName: "Arrival Confirmation",
      filter: true,
      minWidth: 250,
      module: MODULES.Transaction,
      field: "arrivalConfirmation",
      id: "arrivalConfirmation",
      primaryForm: form,
      authenticated: true,
      cellRenderer: SelectRenderer,
      cellRendererParams: (params) => ({
        onChangeHandler: (data) => {
          handleDropdownChange(
            eventKeys.ARRIVAL_CONFIRMATION,
            data,
            params.data,
          );
        },
        authenticated: true,
        module: MODULES.Transaction,
        form: form,
        field: "arrivalConfirmation",
        placeholder: "Arrival Confirmation",
        value: params.data[eventKeys.ARRIVAL_CONFIRMATION],
        options: arrivalConfirmationOpt,
        disabled: !arrivalConfEnStatus.includes(params.data?.staId),
      }),
    },
    {
      headerName: "Route Confirmation",
      filter: true,
      minWidth: 250,
      module: MODULES.Transaction,
      field: "routeConfirmation",
      id: "routeConfirmation",
      primaryForm: form,
      authenticated: true,
      cellRenderer: SelectRenderer,
      cellRendererParams: (params) => ({
        onChangeHandler: (data) => {
          handleDropdownChange(eventKeys.ROUTE_CONFIRMATION, data, params.data);
        },
        authenticated: true,
        module: MODULES.Transaction,
        form: form,
        field: "routeConfirmation",
        placeholder: "Route Confirmation",
        value: params.data[eventKeys.ROUTE_CONFIRMATION],
        options: yesNoOptions,
        disabled: !routeConfEnStatus.includes(params.data?.staId),
      }),
    },
    {
      headerName: "KM Deviation",
      filter: true,
      minWidth: 250,
      module: MODULES.Transaction,
      field: "kmDeviation",
      id: "kmDeviation",
      primaryForm: form,
      authenticated: true,
      cellRenderer: SelectRenderer,
      cellRendererParams: (params) => ({
        onChangeHandler: (data) => {
          handleDropdownChange(eventKeys.KM_DEVIATION, data, params.data);
        },
        authenticated: true,
        module: MODULES.Transaction,
        form: form,
        field: "kmDeviation",
        placeholder: "KM Deviation",
        value: params.data[eventKeys.KM_DEVIATION],
        options: yesNoOptions,
        disabled:
          !kmDeviationEnStatus.includes(params.data?.staId) &&
          !kmDeviationUpdatePermission,
      }),
    },
    {
      headerName: "Flags",
      wrapText: true,
      minWidth: 250,
      module: MODULES.Transaction,
      primaryForm: form,
      secondaryForm: `${FORMS.JOB.FLAG.form}_1`,
      authenticated: true,
      filter: "agTextColumnFilter",
      filterParams: {
        defaultOption: "startsWith",
      },
      cellRenderer: RemarkFlagRenderer,
      field: "gLatestFlag",
      id: "gLatestFlag",
      cellRendererParams: (params) => ({
        type: gridHeaders.FLAG,
        params,
      }),
      valueGetter: (p) => {
        const lf = p.data?.latestFlag;
        if (!lf) return "No Flags";
        const status = lf.split(",")[1]?.trim();
        return status === "Resolved"
          ? "Flags"
          : status === "Accepted"
            ? "Accepted Flag"
            : "Unresolved Flag";
      },
    },
    {
      headerName: "Remarks",
      wrapText: true,
      minWidth: 250,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: RemarkFlagRenderer,
      secondaryForm: `${FORMS.JOB.FLAG.form}_3`,
      field: "gLatestRemark",
      id: "gLatestRemark",
      cellRendererParams: (params) => ({
        type: gridHeaders.REMARKS,
        params,
      }),
    },
    {
      headerName: "ARRD At Pickup Point",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "gFirstArrivalTime",
      id: "gFirstArrivalTime",
      type: "dateCol",
    },
    {
      headerName: "DEP From Pickup Point",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "gFirstDepatureTime",
      id: "gFirstDepatureTime",
      type: "dateCol",
    },
    {
      headerName: "Arrival On Time",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "gArrOnTime",
      id: "gArrOnTime",
    },
    {
      headerName: "ARRD At Delivery Point",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "gLastArrivalTime",
      id: "gLastArrivalTime",
      type: "dateCol",
    },
    {
      headerName: "DEP From Delivery Point",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "gLastDepatureTime",
      id: "gLastDepatureTime",
      type: "dateCol",
    },
    {
      headerName: "Depature On Time",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "gDepOnTime",
      id: "gDepOnTime",
    },
    {
      headerName: "Start Time Gap",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      field: "startTimeGap",
      id: "startTimeGap",
      cellDataType: "text",
    },
    {
      headerName: "Updated By",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      field: "startArrTimeUpdatedBy",
      id: "startArrTimeUpdatedBy",
      cellDataType: "text",
    },
    {
      headerName: "Updated By",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      field: "startDepTimeUpdatedBy",
      id: "startDepTimeUpdatedBy",
      cellDataType: "text",
    },
    {
      headerName: "End Time Gap",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      field: "endTimeGap",
      id: "endTimeGap",
      cellDataType: "text",
    },
    {
      headerName: "Updated By",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      field: "endArrTimeUpdatedBy",
      id: "endArrTimeUpdatedBy",
      cellDataType: "text",
    },
    {
      headerName: "Updated By",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      field: "endDepTimeUpdatedBy",
      id: "endDepTimeUpdatedBy",
      cellDataType: "text",
    },
    {
      headerName: "REQ CBM",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "gReqCbm",
      id: "gReqCbm",
      cellDataType: "text",
    },
    {
      headerName: "JOB CBM",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "gJobCbm",
      id: "gJobCbm",
      cellEditor: "agNumberCellEditor",
      valueParser: (params) => {
        return params.newValue === "" ? null : Number(params.newValue);
      },
      editable: (param) => jCbmEditableList.includes(param.data?.staId),
      cellStyle: (param) => ({
        backgroundColor: jCbmEditableList.includes(param.data?.staId)
          ? "#fff9c4"
          : "#ffffff",
      }),
    },
    {
      headerName: "Km Expected",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      field: "gJobKmExpected",
      id: "gJobKmExpected",
      cellDataType: "text",
    },
    {
      headerName: "Cargo in Hand",
      field: eventKeys.KM_ACTUAL,
      id: "kmActualValue",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      editable: (params) => {
        return (
          params.data[eventKeys.KM_ACTUAL] === null &&
          !jobEditDisabledList.includes(params.data?.staId)
        );
      },
      cellEditor: "agNumberCellEditor",
      valueParser: (params) => {
        return params.newValue === "" ? null : Number(params.newValue);
      },
      cellStyle: (params) => {
        return {
          backgroundColor:
            params.value === null
              ? !jobEditDisabledList.includes(params.data?.staId)
                ? "#fff9c4"
                : ""
              : "",
        };
      },
    },
    {
      headerName: "Chargeable Km",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      id: "gChargeableKm",
      field: "gChargeableKm",
      cellDataType: "text",
    },
    {
      headerName: "Planned Km",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      id: "gPlannedKm",
      field: "gPlannedKm",
      cellDataType: "text",
    },
    {
      minWidth: 200,
      authenticated: true,
      headerName: gridHeaders.DED_FUEL,
      field: "dedFuel",
      id: "dedFuel",
      module: MODULES.Transaction,
      primaryForm: form,
      cellRenderer: ButtonRenderer,
      cellRendererParams: (params) => ({
        clickHandler: () => {
          handleGridButtonClick(params.data, gridHeaders.DED_FUEL);
        },
        label: params.data?.fuelStatus,
        disabled:
          params.data?.fuelStatus === "Fuel Added" ||
          params.data?.staId === jobCompleteStatus ||
          params.data?.gJobVehicle === "Vehicle not assigned",
        className:
          params.data?.fuelStatus === "Fuel Added"
            ? "ui green button"
            : "ui blue button",
        authenticated: true,
        form: form,
        module: MODULES.Transaction,
        editMode: false,
      }),
    },
    {
      field: "jobConfirm",
      id: "jobConfirm",
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      headerName: "Job Confirmation",
      cellRenderer: ButtonRenderer,
      cellRendererParams: (params) => {
        const isConfirmed = params.data?.confStatus === 3;
        const isResolved = params.data?.jobDisputeResolved === 1;
        const isDisputed =
          params.data?.disputed || params.data?.confStatus === 4;

        let label = "Job Confirmation";
        let className = "ui blue button";
        let popupTarget = gridHeaders.JOB_APPROVAL;

        if (isConfirmed) {
          label = "Confirmed";
          className = "ui green button";
        } else if (isResolved) {
          label = "Resolved";
          className = "ui yellow button";
          popupTarget = gridHeaders.JOB_APPROVAL;
        } else if (isDisputed) {
          label = "Disputed";
          className = "ui red button";
          popupTarget = gridHeaders.DISPUTE_RESOLUTION;
        }

        return {
          clickHandler: () => {
            setCurrentJobId(params.data?.jobId);
            setCurrentJobCode(params.data?.jobCode);
            setCurrentRequestId(params.data?.reqId);
            setCurrentCustomer(params.data?.reqCustomerId);
            setCurrentJobResolvedStatus(params.data?.jobDisputeResolved);

            setPopUpContent(popupTarget);
            setPopUpKey((prevState) => prevState + 1);
            modalRef.current?.showModal();
          },
          label: label,
          className: className,
          disabled:
            params.data?.confStatus === 1 ||
            params.data?.confStatus === 3 ||
            params.data[eventKeys.KM_ACTUAL] === null,
          consider: AUTH_TYPES.CREATE,
          authenticated: true,
          form: form,
          module: MODULES.Transaction,
          editMode: false,
          field: "jobConfirm",
          authType: AUTHORITY_TYPE.FIELD,
        };
      },
    },
    {
      field: "costInfo",
      id: "costInfo",
      filter: true,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      cellRenderer: ButtonRenderer,
      cellRendererParams: (_) => ({
        clickHandler: () => {
          // setCurrentCluster(params.data);
        },
        label: "Cost Info",
        disabled: true,
        consider: AUTH_TYPES.CREATE,
        authenticated: true,
        form: form,
        module: MODULES.Transaction,
        editMode: false,
        field: "costInfo",
        authType: AUTHORITY_TYPE.FIELD,
      }),
    },
    {
      field: "financeInfo",
      id: "financeInfo",
      filter: true,
      minWidth: 200,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      cellRenderer: ButtonRenderer,
      cellRendererParams: (_) => ({
        clickHandler: () => {
          // setCurrentCluster(params.data);
        },
        label: "Finance Info",
        disabled: true,
        consider: AUTH_TYPES.CREATE,
        authenticated: true,
        form: form,
        module: MODULES.Transaction,
        editMode: false,
        field: "financeInfo",
        authType: AUTHORITY_TYPE.FIELD,
      }),
    },
    {
      headerName: "Documents Approved",
      filter: true,
      minWidth: 250,
      module: MODULES.Transaction,
      field: "jobDocApproved",
      id: "jobDocApproved",
      primaryForm: form,
      authenticated: false,
      cellRenderer: SelectRenderer,
      cellRendererParams: (params) => ({
        onChangeHandler: (data) => {
          handleDropdownChange(eventKeys.DOC_APPROVED, data, params.data);
        },
        authenticated: false,
        module: MODULES.Transaction,
        form: form,
        field: "jobDocApproved",
        placeholder: "Documents Approved",
        value: params.data[eventKeys.DOC_APPROVED],
        options: yesNoOptions,
        disabled:
          jobEditDisabledList.includes(params.data?.staId) ||
          !params.data?.hasDocs ||
          params.data[eventKeys.DOC_APPROVED] === 1,
      }),
    },
    {
      headerName: "Document Approved Date",
      module: MODULES.Transaction,
      minWidth: 250,
      primaryForm: form,
      authenticated: false,
      field: "docApprovedDate",
      id: "docApprovedDate",
      cellDataType: "text",
      type: "dateCol",
    },
    {
      field: "jobDocUpload",
      id: "jobDocUpload",
      minWidth: 110,
      width: 110,
      module: MODULES.Transaction,
      primaryForm: form,
      secondaryForm: FORMS.JOB.DOCUMENT.form,
      consider: "SECOND_FORM",
      authenticated: true,
      cellRenderer: IconButtonRenderer,
      cellRendererParams: (params) => ({
        clickHandler: () => {
          handleGridButtonClick(params.data, gridHeaders.DOCUMENT);
        },
        icon: <FontAwesomeIcon icon={faFileUpload} />,
        consider: AUTH_TYPES.CREATE,
        authenticated: true,
        form: form,
        module: MODULES.Transaction,
        editMode: false,
        field: "jobDocUpload",
        authType: AUTHORITY_TYPE.FIELD,
        disabled:
          jobEditDisabledList.includes(params.data?.staId) ||
          (params.data?.jobDocFreeze && !ignoreDocFreezePermission),
      }),
    },
    {
      headerName: "Vehicle",
      cellRenderer: VehicleAssignmentStyle,
      // pinned: "right",
      module: MODULES.Transaction,
      primaryForm: form,
      secondaryForm: FORMS.VEHICLE_ASSIGNMENT.VEHICLE.form,
      authenticated: true,
      filter: true,
      minWidth: 140,
      width: 140,
      field: "gJobVehicle",
      id: "gJobVehicle",
    },
    {
      headerName: "Time",
      field: "gLocationStatus",
      id: "gLocationStatus",
      module: MODULES.Transaction,
      primaryForm: form,
      secondaryForm: FORMS.JOB.TIME_UPDATE.form,
      authenticated: true,
      minWidth: 100,
      width: 100,
      cellStyle: (param) => ({
        backgroundColor: PINNED_COLUMN_COLOR,
        color: param.data?.hasReturnRemarks
          ? "#ea777dff"
          : param.data?.hasGpsTimeUpdateSuggestions
            ? "#0bbf8f"
            : "#000",
      }),
    },
    {
      field: "Track",
      id: "Track",
      minWidth: 90,
      width: 90,
      module: MODULES.Transaction,
      primaryForm: form,
      secondaryForm: FORMS.JOB.TRACK.form,
      consider: "SECOND_FORM",
      authenticated: true,
      cellStyle: { backgroundColor: PINNED_COLUMN_COLOR },
      cellRenderer: IconButtonRenderer,
      cellRendererParams: (params) => ({
        clickHandler: () => {
          handleGridButtonClick(params.data, gridHeaders.VEHICLE_STATUS);
        },
        icon:
          params.data?.staAnimation === 0 ? (
            <FontAwesomeIcon icon={faLocationCrosshairs} />
          ) : (
            <FontAwesomeIcon
              style={{ color: "#C72127" }}
              beat
              icon={faLocationCrosshairs}
            />
          ),
        style: { width: "50px" },
      }),
      // pinned: "right",
    },
    {
      headerName: "Current Status",
      field: "gStaName",
      id: "gStaName",
      minWidth: 180,
      width: 180,
      module: MODULES.Transaction,
      primaryForm: form,
      secondaryForm: FORMS.JOB_STATUS.HISTORY.form,
      authenticated: true,
      filter: true,
      cellRenderer: StatusLabelStyle,
      cellStyle: (params) => ({
        backgroundColor: params.data?.staColor || PINNED_COLUMN_COLOR,
      }),
      // pinned: "right",
    },
    {
      headerName: "Vehicle No",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      id: "vehVehicleno",
      field: "vehVehicleno",
      cellDataType: "text",
    },
    {
      headerName: "GPS Availability",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      id: "gpsAvailability",
      field: "gpsAvailability",
    },
    {
      headerName: "Factory Requested Arrival Time",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      id: "arrivalEta",
      field: "arrivalEta",
      // cellDataType: "text",
      type: "dateCol",
    },
    {
      headerName: "Assigned By",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "vehicleAssignedBy",
      field: "vehicleAssignedBy",
      // cellDataType: "text",
    },
    {
      headerName: "Assigned Date",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      id: "vehicleAssignedDate",
      field: "vehicleAssignedDate",
      cellRenderer: ResizableScrollingTextRenderer,
      type: "dateCol",
      // cellDataType: "text",
    },
    {
      headerName: "Time Gap",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      id: "assignedTimeGap",
      field: "assignedTimeGap",
      cellDataType: "text",
    },
    {
      headerName: "Arrival Confirmation Date",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      id: "arrivalConfTime",
      field: "arrivalConfTime",
      type: "dateCol",
      // cellDataType: "text",
    },
    {
      headerName: "Arrival Confirmation BY",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      id: "arrivalConfBy",
      field: "arrivalConfBy",
      cellDataType: "text",
    },
    {
      headerName: "Updated Date/Time",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      id: "startArrivalUpdatedTime",
      field: "startArrivalUpdatedTime",
      type: "dateCol",
      // cellDataType: "text",
    },
    {
      headerName: "Updated Date/Time",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      id: "startDepartureUpdatedTime",
      field: "startDepartureUpdatedTime",
      type: "dateCol",
      // cellDataType: "text",
    },
    {
      headerName: "Updated Date/Time",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      id: "endArrivalUpdatedTime",
      field: "endArrivalUpdatedTime",
      type: "dateCol",
      // cellDataType: "text",
    },
    {
      headerName: "Updated Date/Time",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      id: "endDepartureUpdatedTime",
      field: "endDepartureUpdatedTime",
      type: "dateCol",
      // cellDataType: "text",
    },
    {
      headerName: "Time Gap",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      id: "timeGapRequestApproveAndArrivalEta",
      field: "timeGapRequestApproveAndArrivalEta",
      cellDataType: "text",
    },
    {
      headerName: "Time Gap",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      id: "timeGapFirArrTimeUpdatedTimeAndFirstArrTime",
      field: "timeGapFirArrTimeUpdatedTimeAndFirstArrTime",
      cellDataType: "text",
    },
    {
      headerName: "Time Gap",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      id: "timeGapFirDepTimeUpdatedTimeAndFirstDepTime",
      field: "timeGapFirDepTimeUpdatedTimeAndFirstDepTime",
      cellDataType: "text",
    },
    {
      headerName: "Time Gap",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      id: "timeGapLastArrTimeUpdatedTimeAndLastArrTime",
      field: "timeGapLastArrTimeUpdatedTimeAndLastArrTime",
      cellDataType: "text",
    },
    {
      headerName: "Time Gap",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      id: "timeGapLastDepTimeUpdatedTimeAndLastDepTime",
      field: "timeGapLastDepTimeUpdatedTimeAndLastDepTime",
      cellDataType: "text",
    },
    {
      headerName: "Action",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      id: "actionColumn",
      field: "actionColumn",
      cellDataType: "text",
    },
    {
      headerName: "Total Charge",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "totalRev",
      field: "totalRev",
      // cellDataType: "text",
    },
    {
      headerName: "Transport",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "transportRev",
      field: "transportRev",
      // cellDataType: "text",
    },
    {
      headerName: "Demurrage",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "demurrageRev",
      field: "demurrageRev",
      // cellDataType: "text",
    },
    {
      headerName: "Night Park",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "nightParkRev",
      field: "nightParkRev",
      // cellDataType: "text",
    },
    {
      headerName: "Management",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "managementRev",
      field: "managementRev",
      // cellDataType: "text",
    },
    {
      headerName: "Fuel Adjustment Fee",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "fuelAdjRev",
      field: "fuelAdjRev",
      // cellDataType: "text",
    },
    {
      headerName: "Dropping Point Charges",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "droppingPointRev",
      field: "droppingPointRev",
      // cellDataType: "text",
    },
    {
      headerName: "Unloading Charges",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "unloadingRev",
      field: "unloadingRev",
      // cellDataType: "text",
    },
    {
      headerName: "Load/Unload",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "loadUnloadRev",
      field: "loadUnloadRev",
      // cellDataType: "text",
    },
    {
      headerName: "Lashing Charges",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "lashingRev",
      field: "lashingRev",
      // cellDataType: "text",
    },
    {
      headerName: "Highway Charges",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "highwayRev",
      field: "highwayRev",
      // cellDataType: "text",
    },
    {
      headerName: "Tally Fee",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "tallyRev",
      field: "tallyRev",
      // cellDataType: "text",
    },
    {
      headerName: "BOI Charge",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "boiRev",
      field: "boiRev",
      // cellDataType: "text",
    },
    {
      headerName: "Loading Charges",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "loadingRev",
      field: "loadingRev",
      // cellDataType: "text",
    },
    {
      headerName: "Container Charges",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "containerRev",
      field: "containerRev",
      // cellDataType: "text",
    },
    {
      headerName: "Weighing Charges",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "weighingRev",
      field: "weighingRev",
      // cellDataType: "text",
    },
    {
      headerName: "Discount",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "discountRev",
      field: "discountRev",
      // cellDataType: "text",
    },
    {
      headerName: "Other",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "otherRev",
      field: "otherRev",
      // cellDataType: "text",
    },
    {
      headerName: "Via Locations",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "viaLocations",
      field: "viaLocations",
      cellDataType: "text",
    },
    {
      headerName: "Pickup Contact",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "startContact",
      field: "startContact",
      cellDataType: "text",
    },
    {
      headerName: "Delivery Contact",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "endContact",
      field: "endContact",
      cellDataType: "text",
    },

    {
      headerName: "Parent Request",
      module: MODULES.Transaction,
      authenticated: true,
      cellRenderer: ResizableScrollingTextRenderer,
      primaryForm: form,
      field: "reqParentRequestCode",
      filter: true,
      id: "reqParentRequestCode",
      cellDataType: "text",
    },
    {
      headerName: "FOC",
      minWidth: 150,
      module: MODULES.Transaction,
      primaryForm: form,
      authenticated: true,
      filter: true,
      id: "focStatus",
      field: "focStatus",
      cellDataType: "text",
    },
    {
      headerName: "Pickup Demurrage Reason",
      minWidth: 220,
      module: MODULES.Transaction,
      primaryForm: form,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "pickupDemurrageReason",
      field: "pickupDemurrageReason",
      cellDataType: "text",
    },
    {
      headerName: "Container Number",
      minWidth: 140,
      width: 140,
      module: MODULES.Transaction,
      primaryForm: form,
      cellRenderer: ResizableScrollingTextRenderer,
      id: "containerNumbers",
      field: "containerNumbers",
      cellDataType: "text",
    },
  ];

  const getColumnConfig = useCallback(() => {
    const currentConfig = [];

    const sortedVisible = Object.entries(selectedHeaders)
      .filter(([, val]) => val && val.show)
      .sort(([, a], [, b]) => (a.seq ?? 0) - (b.seq ?? 0));
    console.log("sortedVisible", sortedVisible);
    for (const [key, value] of sortedVisible) {
      const cfg = allGridConfig.find((c) => c.id === key);
      if (cfg)
        currentConfig.push({
          ...cfg,
          pinned: `${value.pinned === "left" ? "left" : value.pinned === "right" ? "right" : ""}`,
        });
    }

    return currentConfig;
  }, [selectedHeaders, isLoading, isSearchResultsPending]);

  useEffect(() => {
    setGridConfig(getColumnConfig());
  }, [getColumnConfig]);

  useEffect(() => {
    (async () => {
      if (params && params.id && params.popup) {
        switch (params.popup) {
          case "gps": {
            const response = await userBasedJobSearch({
              lDataType: null,
              dFromData: null,
              dToDate: null,
              sCustomer: null,
              sShipper: null,
              sJobStatus: null,
              sJobNos: params.id,
              sRequestNos: null,
              sVehicleNos: null,
              sFlaggedType: null,
              sResolved: null,
              sStatus: null,
              sActive: null,
            });
            const currentRes = response.find(
              (job) => job.jobId === parseInt(params.id),
            );
            setCurrentJobId(currentRes.jobId);
            setCurrentJobCode(currentRes.jobCode);
            setCurrentRequestId(currentRes.reqId);
            setCurrentRequestCode(currentRes.reqCode);
            setTrakingEnabled(currentRes.staAnimation === 1);
            setCurrentVehicle(currentRes.jobVehicle);
            setCurrentDriver(currentRes.jobDriver);
            setShowCloseButton(false);
            setLastDepartureTime(currentRes.lastDepartureTime);
            setFirstArrivalTime(currentRes.firstArrivalTime);
            setPopUpContent(gridHeaders.VEHICLE_STATUS);
            modalRef.current?.showModal();
            break;
          }
          default:
        }
      }
    })();
  }, [params]);

  useEffect(() => {
    (async () => {
      if (
        jobConfirmationAlertSearchParams &&
        jobConfirmationAlertSearchParams.id &&
        jobConfirmationAlertSearchParams.type
      ) {
        switch (jobConfirmationAlertSearchParams.type) {
          case "jobConfirmationAlert": {
            await handleSearch({
              lDataType: null,
              dFromData: null,
              dToDate: null,
              sCustomer: null,
              sShipper: null,
              sJobStatus: null,
              sJobNos: jobConfirmationAlertSearchParams.id,
              sRequestNos: null,
              sVehicleNos: null,
              sFlaggedType: null,
              sResolved: null,
              sStatus: null,
              sActive: null,
            });
            break;
          }
          default:
        }
      }
    })();
  }, [
    jobConfirmationAlertSearchParams.id,
    jobConfirmationAlertSearchParams.type,
  ]);

  const handleDrawerOpen = () => {
    setIsDrawerOpen(true);
    setTab(1);
  };
  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    if (
      drawerContent === gridHeaders.REQUEST ||
      drawerContent === gridHeaders.TIME_UPDATE
    ) {
      setUpdateNeeded(true);
    }
    setEvInvMode("");
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      if (
        jobMonitorTemplatePermission &&
        timeUpdateTemplatePermission &&
        chargePanelTemplatePermission
      ) {
        const stored = localStorage.getItem(ACTIVE_TEMPLATE);
        setActiveTemplate(
          stored ? Number(stored) : template.JOB_MONITOR_TEMPLATE,
        );
      } else if (jobMonitorTemplatePermission) {
        const stored = localStorage.getItem(ACTIVE_TEMPLATE);
        setActiveTemplate(
          stored ? Number(stored) : template.JOB_MONITOR_TEMPLATE,
        );
      } else if (timeUpdateTemplatePermission) {
        const stored = localStorage.getItem(ACTIVE_TEMPLATE);
        setActiveTemplate(
          stored ? Number(stored) : template.TIME_UPDATE_TEMPLATE,
        );
      } else if (chargePanelTemplatePermission) {
        const stored = localStorage.getItem(ACTIVE_TEMPLATE);
        setActiveTemplate(
          stored ? Number(stored) : template.CHARGE_PANEL_TEMPLATE,
        );
      } else {
        const stored = localStorage.getItem(ACTIVE_TEMPLATE);
        setActiveTemplate(stored ? Number(stored) : template.NO_TEMPLATE);
      }

      const { arrival_confirmation: arrivalConfirmationOptions, yesno: ynOpt } =
        await getBulkDropdownData([
          { key: "arrival_confirmation" },
          { key: "yesno" },
        ]);

      setArrivalConfirmationOpt(arrivalConfirmationOptions);
      setYesNoOptions(ynOpt);

      const {
        [STATUS_GROUPS.KM_CALCULATION_ACTION]: kmCalcList = [],
        [STATUS_GROUPS.KM_EDITABLE]: mappedKCEList = [],
        [STATUS_GROUPS.OPERATION_COMPLETE]: operationCompleteStatus = [],
        [STATUS_GROUPS.JOB_COMPLETED]: jobCompleteStatus = [],
        [STATUS_GROUPS.JOB_DISPUTED]: jobDisputeStatus = [],
        [STATUS_GROUPS.REQUEST_VIEW]: mappedREEList = [],
        [STATUS_GROUPS.TIME_UPDATE_ENABLED]: mappedTUEList = [],
        [STATUS_GROUPS.AD_HOC_EV_INV]: evInvAdHocEnableList = [],
        [STATUS_GROUPS.ARRIVAL_CONFIRMATION_ENABLED]: arrConfEnableList = [],
        [STATUS_GROUPS.ROUTE_CONFIRMATION_ENABLED]: routeConfEnableList = [],
        [STATUS_GROUPS.KM_DEVIATION_ENABLED]: kmDeviationEnableList = [],
        [STATUS_GROUPS.JOB_EDIT_DISABLED]: jedList = [],
        [STATUS_GROUPS.KM_ADDED]: kmAddedList = [],
        [userData.type === 2
          ? STATUS_GROUPS.RELEASE_NOTE_CUSTOMER
          : userData.type === 3
            ? STATUS_GROUPS.RELEASE_NOTE_TRANSPORTER
            : STATUS_GROUPS.RELEASE_NOTE_INTERNAL]: releaseNoteEnableList = [],
        [STATUS_GROUPS.LOCATION_BASED_RELEASE_NOTE]:
          locationBasedReleaseNoteEnableList = [],
        [userData.type === 2
          ? STATUS_GROUPS.CUSTOMER_STATUS
          : userData.type === 3
            ? STATUS_GROUPS.TRANSPORTER_STATUS
            : STATUS_GROUPS.ALL]: jobStatusData = [],
        [STATUS_GROUPS.JOB_CBM_EDITABLE]: jobCbmEditableList = [],
        [userData.type === 2
          ? STATUS_GROUPS.CUSTOMER_PO_NO_EDIT
          : userData.type === 3
            ? STATUS_GROUPS.TRANSPORTER_PO_NO_EDIT
            : STATUS_GROUPS.INTERNAL_USER_PO_NO_EDIT]: poNoEnableList = [],
        [STATUS_GROUPS.FOC_ALLOWED]: focEnableList = [],
      } = await statusIdsByContexts([
        STATUS_GROUPS.KM_CALCULATION_ACTION,
        STATUS_GROUPS.KM_EDITABLE,
        STATUS_GROUPS.OPERATION_COMPLETE,
        STATUS_GROUPS.JOB_COMPLETED,
        STATUS_GROUPS.JOB_DISPUTED,
        STATUS_GROUPS.REQUEST_VIEW,
        STATUS_GROUPS.TIME_UPDATE_ENABLED,
        STATUS_GROUPS.AD_HOC_EV_INV,
        STATUS_GROUPS.ARRIVAL_CONFIRMATION_ENABLED,
        STATUS_GROUPS.ROUTE_CONFIRMATION_ENABLED,
        STATUS_GROUPS.KM_DEVIATION_ENABLED,
        userData.type === 2
          ? STATUS_GROUPS.RELEASE_NOTE_CUSTOMER
          : userData.type === 3
            ? STATUS_GROUPS.RELEASE_NOTE_TRANSPORTER
            : STATUS_GROUPS.RELEASE_NOTE_INTERNAL,
        STATUS_GROUPS.LOCATION_BASED_RELEASE_NOTE,
        userData.type === 2
          ? STATUS_GROUPS.CUSTOMER_STATUS
          : userData.type === 3
            ? STATUS_GROUPS.TRANSPORTER_STATUS
            : STATUS_GROUPS.ALL,
        STATUS_GROUPS.JOB_EDIT_DISABLED,
        STATUS_GROUPS.KM_ADDED,
        STATUS_GROUPS.JOB_CBM_EDITABLE,
        userData.type === 2
          ? STATUS_GROUPS.CUSTOMER_PO_NO_EDIT
          : userData.type === 3
            ? STATUS_GROUPS.TRANSPORTER_PO_NO_EDIT
            : STATUS_GROUPS.INTERNAL_USER_PO_NO_EDIT,
        STATUS_GROUPS.FOC_ALLOWED,
      ]);

      kmCalcActionIdsRef.current = kmCalcList;
      operationCompleteStatusRef.current = operationCompleteStatus
        ? operationCompleteStatus[0]
        : null;
      setOperationCompleteStatus(
        operationCompleteStatus ? operationCompleteStatus[0] : null,
      );
      setJobCompleteStatus(jobCompleteStatus ? jobCompleteStatus[0] : null);
      setJobDisputeStatus(jobDisputeStatus ? jobDisputeStatus[0] : null);
      setKmCalculationEnabledStatus(mappedKCEList);
      setRequestEditModeEnabledIds(mappedREEList);
      setTueList(mappedTUEList);
      setJobStatusList(jobStatusData);
      setArrivalConfEnStatus(arrConfEnableList);
      setRouteConfEnStatus(routeConfEnableList);
      setKmDeviationEnStatus(kmDeviationEnableList);
      setJobEditDisabledList(jedList);
      setKmAdded(kmAddedList);
      setJCbmEditableList(jobCbmEditableList);
      setPoNoEditableList(poNoEnableList);

      adHocEvInvEnableListRef.current = evInvAdHocEnableList;
      releaseNoteEnableListRef.current = releaseNoteEnableList;
      locationBasedReleaseNoteEnableListRef.current =
        locationBasedReleaseNoteEnableList;
      focEnabledListRef.current = focEnableList;

      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (updateNeeded) {
        setDefaultFilter(gridRef.current?.getFilterText());
        await handleSearch(currentSearchData);
        setUpdateNeeded(false);
      }
    })();
  }, [updateNeeded]);

  const closePopup = (reload = true) => {
    modalRef.current.close();
    if (reload) {
      setUpdateNeeded(true);
    }
  };

  const openReqCopyModal = () => {
    console.log("[DEBUG_REQUEST_COPY] Opening the Modal");
    modalRef.current?.showModal();
  };

  const closeReqCopyModal = (response) => {
    modalRef.current?.close();

    // Wait until after modal closes
    setTimeout(() => {
      if (response && response.reqCode) {
        toast.success(
          `Request copy created successfully (Request Code: ${response.reqCode})`,
          {
            autoClose: 5000,
            containerId: "Main",
          },
        );
      }
    }, 300); // Small delay to let modal unmount first
  };

  const jobConfirmHandler = async (data) => {
    try {
      const message = await jobConfirmation({
        username: user.username,
        job: data?.jobId,
        request: data?.reqId,
        code: "JBC",
        active: data?.active,
        dispute: data?.dispute,
        remark: data?.remark,
        stakeholder: data?.reqCustomerId,
        currency: userCurrency,
      });

      if (!message) throw new Error("No message returned");

      setSearchResults((prevResults) =>
        prevResults.map((job) =>
          job.jobId === data?.jobId
            ? { ...job, staId: 22, disputed: false }
            : job,
        ),
      );
    } catch (e) {
      throw e;
    }
  };

  let drawerComponent;
  let headerComponent = <></>;
  switch (drawerContent) {
    case gridHeaders.TIME_UPDATE: {
      drawerComponent = (
        <TimeUpdate
          timeUpdateViewMode={timeUpdateViewMode}
          jobId={currentJobId}
          jobInfo={jobInfo}
          jobStatus={jobInfo?.status}
          jobStatusColor={jobInfo?.statusColor}
          jobCode={currentJobCode}
          requestId={currentRequestId}
          requestCode={currentRequestCode}
          customerId={currentCustomer}
          handleDrawerClose={handleDrawerClose}
          setUpdateNeeded={setUpdateNeeded}
          bookingRequiredDate={bookingRequiredDate}
        />
      );
      break;
    }
    case gridHeaders.REQUEST: {
      headerComponent = (
        <h3 className="ui header">Request Code: {currentRequestCode}</h3>
      );
      drawerComponent = (
        <RequestCreationRefactor
          id={currentRequestId}
          key={popUpKey}
          viewOnlyMode={requestViewMode}
          cleanUp={true}
          cleanUpFunction={() => {
            handleDrawerClose();
          }}
          jobId={currentJobId}
          context={3}
        />
      );
      break;
    }
    case gridHeaders.JOB: {
      drawerComponent = (
        <div className="ui segment">
          <h3 className="ui header">Request Code: {currentRequestCode}</h3>
          <RequestCreationRefactor
            id={currentRequestId}
            key={popUpKey}
            viewOnlyMode={requestViewMode}
            cleanUp={true}
            cleanUpFunction={() => {
              handleDrawerClose();
            }}
            jobId={currentJobId}
            context={3}
          />
        </div>
      );
      break;
    }
    case gridHeaders.DED_FUEL: {
      drawerComponent = (
        <div className="ui segment">
          <h3 className="ui header">Request Code: {currentRequestCode}</h3>
          <FuelQuota />
        </div>
      );
      break;
    }
    case "EV_INV_CREATION": {
      headerComponent = (
        <div className="drawer-header">
          <div style={{ padding: "10px 0 10px 0" }}>
            <h3>Add Charges To : {selectedRows[0]?.jobCode}</h3>
          </div>
        </div>
      );
      drawerComponent = (
        <JobMonitorEvInvCreation
          jobId={selectedRows ? selectedRows[0]?.jobId : null}
          template={currentTemplate}
          editMode={false}
          handleDrawerClose={handleDrawerClose}
          evInv={evInvMode}
          modeObj={mode}
          approvalMode={false}
        />
      );
      break;
    }
    default: {
      drawerComponent = <></>;
    }
  }

  let popUpComponent;

  switch (popUpContent) {
    case gridHeaders.REQUEST_COPY: {
      popUpComponent = (
        <RequestCopyModal
          key={popUpKey}
          jobId={currentJobId}
          reqId={currentRequestId}
          close={closeReqCopyModal}
        />
      );
      break;
    }

    case gridHeaders.LIVE_TRACK_LINK:{
      popUpComponent=(
        <JobTrackLink
        key={popUpKey}
        jobId={currentJobId}
        close={closePopup}
        />
      );
      break;
    }

    case gridHeaders.DED_FUEL: {
      popUpComponent = (
        <FuelQuota
          jobId={currentJobId}
          jobCode={currentJobCode}
          stakeholder={currentTransporter}
          vehicleId={currentVehicle}
          close={closePopup}
          type={6}
          errors={{}}
        />
      );
      break;
    }

    case gridHeaders.REPORT_TYPE: {
      popUpComponent = (
        <JobMonitorReportComponent
          key={popUpKey}
          selectedJob={selectedRows[0]}
          releaseNoteVariant={releaseNoteVariant}
        />
      );
      break;
    }
    case gridHeaders.SUPPLER_NEGO: {
      popUpComponent = (
        <JobMonitorNegotiation
          key={popUpKey}
          jobId={currentJobId}
          jobCode={currentJobCode}
          requestCode={currentRequestCode}
          requestId={currentRequestId}
          currentStatus={currentStatus}
          type={4}
          stakeholder={currentTransporter}
          customer={currentCustomer}
          close={closePopup}
          errors={{}}
        />
      );
      break;
    }
    case gridHeaders.CUSTOMER_NEGO: {
      popUpComponent = (
        <JobMonitorNegotiation
          key={popUpKey}
          jobId={currentJobId}
          jobCode={currentJobCode}
          requestCode={currentRequestCode}
          requestId={currentRequestId}
          currentStatus={currentStatus}
          type={1}
          close={closePopup}
          stakeholder={currentCustomer}
          customer={currentCustomer}
          errors={{}}
        />
      );
      break;
    }
    case gridHeaders.JOB_APPROVAL:
      popUpComponent = (
        <JobConfirmation
          key={popUpKey}
          jobId={currentJobId}
          jobCode={currentJobCode}
          reqId={currentRequestId}
          requestCode={currentRequestCode}
          stakeholder={currentCustomer}
          close={closePopup}
          type={"1"}
          flagType={"9"}
          errors={{}}
          resolvedStatus={currentJobResolvedStatus}
        />
      );
      break;
    case gridHeaders.FLAG: {
      popUpComponent = (
        <JobMonitorFlag
          key={popUpKey}
          jobId={currentJobId}
          jobCode={currentJobCode}
          requestCode={currentRequestCode}
          requestId={currentRequestId}
          currentStatus={currentStatus}
          close={closePopup}
          type={"1"}
          errors={{}}
        />
      );
      break;
    }
    case gridHeaders.REMARKS: {
      popUpComponent = (
        <JobMonitorFlag
          key={popUpKey}
          jobId={currentJobId}
          jobCode={currentJobCode}
          requestCode={currentRequestCode}
          requestId={currentRequestId}
          currentStatus={currentStatus}
          close={closePopup}
          type={"3"}
          errors={{}}
        />
      );
      break;
    }
    case gridHeaders.VEHICLE_INFO: {
      popUpComponent = (
        <VehicleAssignment
          key={popUpKey}
          jobId={currentJobId}
          jobCode={currentJobCode}
          requestCode={currentRequestCode}
          requestId={currentRequestId}
          jobCurrentStatus={currentStatus}
          type={currentVehicleType}
          close={closePopup}
          errors={{}}
          staId={currentStaId}
          vehicleTypeId={currentVehicleTypeId}
          containerTypeId={currentContainerTypeId}
          onBackhaulRejected={() => {
            setSearchResults((prevResults) =>
              prevResults.map((job) =>
                job.jobId === currentJobId
                  ? { ...job, reqIsBackhaul: false, backhaulRequestCode: null }
                  : job,
              ),
            );
          }}
        />
      );
      break;
    }
    case gridHeaders.STATUS: {
      popUpComponent = (
        <JobStatusManagement
          key={popUpKey}
          jobId={currentJobId}
          jobCode={currentJobCode}
          requestId={currentRequestId}
          requestCode={currentRequestCode}
          status={currentStatus}
          close={closePopup}
          currentTimeFreeze={timeFreeze}
          errors={{}}
        />
      );
      break;
    }
    case gridHeaders.VEHICLE_STATUS: {
      popUpComponent = (
        <JobVehicleStatus
          key={popUpKey}
          jobId={currentJobId}
          requestId={currentRequestId}
          jobCode={currentJobCode}
          close={closePopup}
          vehicleId={currentVehicle}
          driverId={currentDriver}
          trackingEnabled={trackingEnabled}
          wsConnection={wsConnection}
          timeUpdateCompleted={lastDepartureTime !== null}
          lastDepartureTime={lastDepartureTime}
          firstArrivalTime={firstArrivalTime}
        />
      );
      break;
    }
    case gridHeaders.DOCUMENT: {
      popUpComponent = (
        <JobUpload key={popUpKey} jobId={currentJobId} close={closePopup} />
      );
      break;
    }
    case gridHeaders.KM_ACTUAL: {
      popUpComponent = (
        <JobKmMethodSelection
          key={popUpKey}
          jobId={currentJobId}
          reqId={currentRequestId}
          customerId={currentCustomer}
          close={closePopup}
          viewMode={kmMethodViewOnly}
          kmActual={kmActualAmount}
        />
      );
      break;
    }
    // case gridHeaders.COLUMN_CONFIG: {
    //   popUpComponent = (
    //     <HeaderSelector
    //       key={popUpKey}
    //       close={closeHeaderConfig}
    //       headerList={
    //         activeTemplate === template.JOB_MONITOR_TEMPLATE
    //           ? jobMonitorHeaderList
    //           : activeTemplate === template.TIME_UPDATE_TEMPLATE
    //             ? timeUpdateHeaderList
    //             : activeTemplate === template.CHARGE_PANEL_TEMPLATE
    //               ? chargePanelHeaderList
    //               : {}
    //       }
    //       selectedHeaders={selectedHeaders}
    //       setSelectedHeaders={setSelectedHeaders}
    //       activeTemplate={activeTemplate}
    //       templates={template}
    //       STORAGE_KEY={STORAGE_KEY}
    //     />
    //   );
    //   break;
    // }

    case gridHeaders.DISPUTE_RESOLUTION: {
      popUpComponent = (
        <JobDisputeResolution
          key={popUpKey}
          jobId={currentJobId}
          reqId={currentRequestId}
          close={closePopup}
          onSuccess={handleDisputeResolutionSuccess}
        />
      );
      break;
    }
    default:
      popUpComponent = <></>;
  }

  const formatToTwoDecimalPlaces = (value) => {
    if (value == null || value === "N/A") {
      return null; // or return 0 if you prefer
    }

    const num = parseFloat(value);

    if (isNaN(num)) {
      return null; // or return 0
    }

    return Number(num.toFixed(2));
  };

  const handleSearch = async (data) => {
    setCurrentSearchData(data);
    setSearchClicked(true);
    setShowActionMenu(false);
    setSearchResultsPending(true);
    const response = await userBasedJobSearch(data);

    const mappedResponses = [];
    if (response) {
      setSearchedDateTime(new Date());
      for (let res of response) {
        let remark;
        if (res.latestRemark) {
          const remarkArray = res.latestRemark.split(",");
          remark = `Remark - ${remarkArray[0] ? remarkArray[0] : "N/A"} 
                    CreatedBy - ${remarkArray[1]}
                    Resolved - ${remarkArray[2]}`;
        }

        const kmCalculationEditable = res.staId
          ? kmCalculationEnabledStatus.includes(parseInt(res.staId))
          : false;

        const kmAlreadyAdded = res.staId
          ? kmAdded.includes(parseInt(res.staId))
          : false;

        const kmField = kmCalculationEditable
          ? res.kmMethodCount === 0
            ? res.jobKmActual ?? null
            : res.kmMethodCount === 1
              ? res.jobKmActual
              : res.jobKmActual ?? res.kmMethodLabel
          : kmAlreadyAdded
            ? res.jobKmActual
            : "N/A";

        const mappedRes = {
          jobId: res.jobId ? res.jobId : null,
          reqId: res.reqId ? res.reqId : null,
          reqCode: res.reqCode ? res.reqCode : null,
          jobCode: res.jobCode ? res.jobCode : null,
          reqCustomerId: res.reqCustomerId ? res.reqCustomerId : null,
          reqPlantId: res.reqPlantId ? res.reqPlantId : null,
          jobTransporterId: res.jobTransporter ? res.jobTransporter : null,
          rateType: res.rateType ? res.rateType : null,
          vhtType: res.vhtType ? res.vhtType : null,
          latestFlag: res.latestFlag ? res.latestFlag : null,
          latestRemark: res.latestRemark ? res.latestRemark : null,
          creatorRemark: res.creatorRemark ? res.creatorRemark : null,
          hasCreatorRemark: !!res.creatorRemark,
          staId: res.staId ? res.staId : null,
          jobVehicle: res.jobVehicle ? res.jobVehicle : null,
          jobDriver: res.jobDriver ? res.jobDriver : null,
          staAnimation: res.staAnimation ? res.staAnimation : 0,
          reqVehicleType: res.reqVehicleType ? res.reqVehicleType : null,
          reqContainerType: res.reqContainerType ? res.reqContainerType : null,
          gpsActive: res.gpsActive ? res.gpsActive : false,
          reqBookingRequiredDate: res.reqBookingRequiredDate ?? null,
          kmMethodCount: res.kmMethodCount ?? null,
          docCount: res.docCount ? res.docCount : 0,
          hasDocs: res.docCount ? res.docCount > 0 : false,
          reqDocCount: res.reqDocCount ? res.reqDocCount : 0,
          hasReqDocs: res.reqDocCount ? res.reqDocCount > 0 : false,
          supNegoCount: res.supNegoCount ? res.supNegoCount : 0,
          hasSupNego: res.supNegoCount ? res.supNegoCount > 0 : false,
          cusNegoCount: res.cusNegoCount ? res.cusNegoCount : 0,
          hasCusNego: res.cusNegoCount ? res.cusNegoCount > 0 : false,
          behalfOfUser: res.behalfOfUser ? res.behalfOfUser : "N/A",
          jobCreatedBy: res.jobCreatedBy ? res.jobCreatedBy : "N/A",
          jobCreatedDate: res.jobCreatedDate
            ? oppFormatFromJsonDate(res.jobCreatedDate)
            : "N/A",
          // Grid lables
          gReqCode: res.reqCode ? res.reqCode : "N/A",
          gJobCode: res.jobCode ? res.jobCode : "N/A",
          gCreatedDate: res.createdDate
            ? oppFormatFromJsonDate(res.createdDate)
            : "N/A",
          gCreatedBy: res.createdBy ? res.createdBy : "N/A",
          gReqCustomer: res.customerName ? res.customerName : "N/A",
          gReqBillingCustomer: res.billingCustomer
            ? res.billingCustomer
            : "N/A",
          gReqInvoicingCustomer: res.invoicingCustomer
            ? res.invoicingCustomer
            : "N/A",
          gReqPlant: res.plantName ? res.plantName : "N/A",
          gJobTransporter: res.stkName ? res.stkName : "N/A",
          gDriverName: res.driverName ? res.driverName : "N/A",
          isDriverBirthdayToday: res.isDriverBirthdayToday
            ? res.isDriverBirthdayToday
            : false,
          gHelperName: res.helperName ? res.helperName : "N/A",
          gReqBookingApprovalDate: res.reqBookingApprovalDate
            ? oppFormatFromJsonDate(res.reqBookingApprovalDate)
            : "N/A",
          gReqBookingApprovedBy: res.reqBookingApprovedBy
            ? res.reqBookingApprovedBy
            : "N/A",

          gFirstArrivalTime: res.firstArrivalTime
            ? oppFormatFromJsonDate(res.firstArrivalTime)
            : "N/A",
          gFirstDepatureTime: res.firstDepartureTime
            ? oppFormatFromJsonDate(res.firstDepartureTime)
            : "N/A",
          gLastArrivalTime: res.lastArrivalTime
            ? oppFormatFromJsonDate(res.lastArrivalTime)
            : "N/A",
          gLastDepatureTime: res.lastDepartureTime
            ? oppFormatFromJsonDate(res.lastDepartureTime)
            : "N/A",
          lastDepartureTime: res.lastDepartureTime ?? null,
          firstArrivalTime: res.firstArrivalTime ?? null,
          gReqCbm: res.reqCbm ? res.reqCbm : "N/A",
          gJobCbm: res.jobCbm ? res.jobCbm : "",
          gStaName: res.gridStatusName,
          gJobVehicle: res.vehVehicleno
            ? res.vehVehicleno
            : "Vehicle not assigned",
          gJobMoux: res.jobMoux ? res.jobMoux : "",
          [eventKeys.STATUS]: res[eventKeys.STATUS]
            ? res[eventKeys.STATUS]
            : null,
          gVhtType: res.vhtType ? res.vhtType : "N/A",
          otherCharges: res.otherCharges ? res.otherCharges : "N/A",
          shipmentType: res.shipmentType ? res.shipmentType : "N/A",
          consignmentType: res.consignmentType ? res.consignmentType : "N/A",
          consignee: res.consignee ? res.consignee : "N/A",
          reqKagNoValue: res.reqKagNoValue ? res.reqKagNoValue : "N/A",
          containerNumbers: res.containerNumbers ? res.containerNumbers : "N/A",
          gLatestFlag: res.latestFlag ? res.latestFlag : "No Flags",
          gLatestRemark: res.latestRemark ? remark : "No Remarks",
          gLocationStatus: res.locationStatus ? res.locationStatus : "0 of 10",
          gArrOnTime: res.arrOntime ? res.arrOntime : "N/A",
          gDepOnTime: res.depOntime ? res.depOntime : "N/A",
          gDrvLicenceNo: res.drvLicenceNo ? res.drvLicenceNo : "N/A",
          gDrvPhoneNo: res.drvPhoneNo ? res.drvPhoneNo : "N/A",
          gHelNicNo: res.helNicNo ? res.helNicNo : "N/A",
          gHelPhoneNo: res.helPhoneNo ? res.helPhoneNo : "N/A",
          gJobKmExpected: res.jobKmExpected ? res.jobKmExpected : "N/A",
          gChargeableKm: res.jobChargeableKm ? res.jobChargeableKm : "N/A",
          gPlannedKm: res.reqPlannedKm ? Math.round(res.reqPlannedKm) : "N/A",
          hasKmActual: !!res.jobKmActual,
          hasChargeableKm: !!res.jobChargeableKm,
          jobDocApproved: res.jobDocApproved ?? null,
          docApprovedDate: res.jobDocApprovedDate
            ? oppFormatFromJsonDate(res.jobDocApprovedDate)
            : "N/A",

          returnRemarks: res.jobReturnRemarks ? res.jobReturnRemarks : "N/A",
          returnRemarkCount: res.jobReturnRemarkCount
            ? res.jobReturnRemarkCount
            : 0,
          hasReturnRemarks: res.jobReturnRemarkCount
            ? res.jobReturnRemarkCount > 0
            : false,

          startArrTimeUpdatedBy: res.startArrTimeUpdatedBy
            ? res.startArrTimeUpdatedBy
            : "N/A",
          startDepTimeUpdatedBy: res.startDepTimeUpdatedBy
            ? res.startDepTimeUpdatedBy
            : "N/A",
          startTimeGap: res.startTimeGap
            ? convertSecondsToHMS(res.startTimeGap)
            : "N/A",
          endArrTimeUpdatedBy: res.endArrTimeUpdatedBy
            ? res.endArrTimeUpdatedBy
            : "N/A",
          endDepTimeUpdatedBy: res.endDepTimeUpdatedBy
            ? res.endDepTimeUpdatedBy
            : "N/A",
          endTimeGap: res.endTimeGap
            ? convertSecondsToHMS(res.endTimeGap)
            : "N/A",

          startLocation: res.startLocName ? res.startLocName : "N/A",
          endLocation: res.endLocName ? res.endLocName : "N/A",
          reqLateAccept: res.reqLateAccept ? res.reqLateAccept : "N/A",
          reqLateBooking: res.reqLateBooking ? res.reqLateBooking : "N/A",
          hasGpsTimeUpdateSuggestions: res.hasGpsTimeUpdateSuggestions
            ? res.hasGpsTimeUpdateSuggestions
            : false,

          // Dropdowns
          jobOnTime: res.jobOnTime ? res.jobOnTime : null,

          jobArrivalConfirmation: res.jobArrivalConfirmation
            ? res.jobArrivalConfirmation
            : null,
          jobRouteConfirm: res.jobRouteConfirm ? res.jobRouteConfirm : null,
          kmDeviation: res.kmDeviation ? res.kmDeviation : null,

          // Editable Fields
          jobAod: res.jobAod ? res.jobAod : "",
          jobCallCenterRemark: res.jobCallCenterRemark
            ? res.jobCallCenterRemark
            : "",
          jobOtdRemark: res.jobOtdRemark ? res.jobOtdRemark : "",
          jobAdditionalNo: res.jobAdditionalNo ? res.jobAdditionalNo : "",
          jobSeal1: res.jobSeal1 ? res.jobSeal1 : "",
          jobSeal2: res.jobSeal2 ? res.jobSeal2 : "",
          jobSeal3: res.jobSeal3 ? res.jobSeal3 : "",
          jobMoux: res.jobMoux ? res.jobMoux : "",
          jobCartoon: res.jobCartoon ? res.jobCartoon : "",
          jobKmActual: kmField,
          jobReference: res.jobReference ? res.jobReference : "",
          jobVehAssignRemark: res.jobVehAssignRemark
            ? res.jobVehAssignRemark
            : "",
          jobOperationRemark: res.jobOperationRemark
            ? res.jobOperationRemark
            : "",
          jobCustomerFeedback: res.jobCustomerFeedback
            ? res.jobCustomerFeedback
            : "",
          jobHeldUpRemark: res.jobHeldUpRemark ? res.jobHeldUpRemark : "",

          // Grid Styles
          staColor: res.gridStatusColor,

          //fuel status
          fuelStatus: res.jobVehicle
            ? res.deductionStatus !== null && res.deductionStatus !== ""
              ? "Fuel Added"
              : "Add Fuel"
            : "Add Fuel",
          arrivalEta: res.arrivalEta
            ? oppFormatFromJsonDate(res.arrivalEta)
            : "N/A",
          deliveryEta: res.deliveryEta
            ? oppFormatFromJsonDate(res.deliveryEta)
            : "N/A",
          vehicleAssignedBy: res.vehicleAssignedBy
            ? res.vehicleAssignedBy
            : "N/A",
          vehicleAssignedDate: res.vehicleAssignedDate
            ? oppFormatFromJsonDate(res.vehicleAssignedDate)
            : "N/A",
          reqDeliveryCutOffTime: res.reqDeliveryCutOffTime
            ? oppFormatFromJsonDate(res.reqDeliveryCutOffTime)
            : "N/A",
          reqInvoiceRef: res.reqInvoiceRef ? res.reqInvoiceRef : "",
          gContactPerson: res.reqContactPerson
            ? `${res.reqContactPerson}${res.reqContactNumber ? " - " + res.reqContactNumber : ""}`
            : "N/A",
          assignedTimeGap: res.assignedTimeGap ? res.assignedTimeGap : "N/A",
          driverNic: res.driverNic ? res.driverNic : "N/A",
          arrivalConfTime: res.arrivalConfTime
            ? oppFormatFromJsonDate(res.arrivalConfTime)
            : "N/A",
          arrivalConfBy: res.arrivalConfBy ? res.arrivalConfBy : "N/A",
          startArrivalUpdatedTime: res.startArrivalUpdatedTime
            ? oppFormatFromJsonDate(res.startArrivalUpdatedTime)
            : "N/A",
          startDepartureUpdatedTime: res.startDepartureUpdatedTime
            ? oppFormatFromJsonDate(res.startDepartureUpdatedTime)
            : "N/A",
          endArrivalUpdatedTime: res.endArrivalUpdatedTime
            ? oppFormatFromJsonDate(res.endArrivalUpdatedTime)
            : "N/A",
          endDepartureUpdatedTime: res.endDepartureUpdatedTime
            ? oppFormatFromJsonDate(res.endDepartureUpdatedTime)
            : "N/A",
          vehVehicleno: res.vehVehicleno ? res.vehVehicleno : "N/A",
          gpsAvailability: res.gpsActive ? "YES" : "No",
          timeGapRequestApproveAndArrivalEta:
            res.timeGapRequestApproveAndArrivalEta
              ? res.timeGapRequestApproveAndArrivalEta
              : "N/A",
          timeGapFirArrTimeUpdatedTimeAndFirstArrTime:
            res.timeGapFirArrTimeUpdatedTimeAndFirstArrTime
              ? res.timeGapFirArrTimeUpdatedTimeAndFirstArrTime
              : "N/A",
          timeGapFirDepTimeUpdatedTimeAndFirstDepTime:
            res.timeGapFirDepTimeUpdatedTimeAndFirstDepTime
              ? res.timeGapFirDepTimeUpdatedTimeAndFirstDepTime
              : "N/A",
          timeGapLastArrTimeUpdatedTimeAndLastArrTime:
            res.timeGapLastArrTimeUpdatedTimeAndLastArrTime
              ? res.timeGapLastArrTimeUpdatedTimeAndLastArrTime
              : "N/A",
          timeGapLastDepTimeUpdatedTimeAndLastDepTime:
            res.timeGapLastDepTimeUpdatedTimeAndLastDepTime
              ? res.timeGapLastDepTimeUpdatedTimeAndLastDepTime
              : "N/A",
          actionColumn: res.actionColumn ? res.actionColumn : "N/A",
          reqPoNo: res.jobPonumber != null ? res.jobPonumber : (res.reqPoNo ?? ""),
          conMobile: res.conMobile ? res.conMobile : "N/A",
          transporterContact: res.stkName
            ? `${res.stkName} ${res.conMobile}`
            : "N/A",
          jobArrStatus: res.jobArrStatus ? res.jobArrStatus : "",
          totalRev: res.totalRev ? formatToTwoDecimalPlaces(res.totalRev) : "",
          transportRev: res.transportRev
            ? formatToTwoDecimalPlaces(res.transportRev)
            : "",
          demurrageRev: res.demurrageRev
            ? formatToTwoDecimalPlaces(res.demurrageRev)
            : "",
          nightParkRev: res.nightParkRev
            ? formatToTwoDecimalPlaces(res.nightParkRev)
            : "",
          managementRev: res.managementRev
            ? formatToTwoDecimalPlaces(res.managementRev)
            : "",
          fuelAdjRev: res.fuelAdjRev
            ? formatToTwoDecimalPlaces(res.fuelAdjRev)
            : "",
          droppingPointRev: res.droppingPointRev
            ? formatToTwoDecimalPlaces(res.droppingPointRev)
            : "",
          unloadingRev: res.unloadingRev
            ? formatToTwoDecimalPlaces(res.unloadingRev)
            : "",
          loadUnloadRev: res.loadUnloadRev
            ? formatToTwoDecimalPlaces(res.loadUnloadRev)
            : "",
          lashingRev: res.lashingRev
            ? formatToTwoDecimalPlaces(res.lashingRev)
            : "",
          highwayRev: res.highwayRev
            ? formatToTwoDecimalPlaces(res.highwayRev)
            : "",
          tallyRev: res.tallyRev ? formatToTwoDecimalPlaces(res.tallyRev) : "",
          boiRev: res.boiRev ? formatToTwoDecimalPlaces(res.boiRev) : "",
          loadingRev: res.loadingRev
            ? formatToTwoDecimalPlaces(res.loadingRev)
            : "",
          containerRev: res.containerRev
            ? formatToTwoDecimalPlaces(res.containerRev)
            : "",
          weighingRev: res.weighingRev
            ? formatToTwoDecimalPlaces(res.weighingRev)
            : "",
          discountRev: res.discountRev
            ? formatToTwoDecimalPlaces(res.discountRev)
            : "",
          otherRev: res.otherRev ? formatToTwoDecimalPlaces(res.otherRev) : "",
          monitoringRev: res.monitoringRev
            ? formatToTwoDecimalPlaces(res.monitoringRev)
            : "",
          demmurrageKmHours: res.demmurrageKmHours ? res.demmurrageKmHours : "",
          viaLocations: res.viaLocations ? res.viaLocations : "",
          startContact: res.startContact ? res.startContact : "",
          endContact: res.endContact ? res.endContact : "",
          confStatus: res.confStatus ?? 1,
          reqParentRequestCode: res.reqParentRequestCode
            ? res.reqParentRequestCode
            : "",
          jobDisputeResolved: res.jobDisputeResolved,
          containerNo: res.reqContainerNo ? res.reqContainerNo : "",
          reqContactNo: res.reqContactNumber ? res.reqContactNumber : "",
          actualKm: res.jobKmActual ? res.jobKmActual : "",
          hasGpsCalc: res.hasGpsCalc ?? false,
          jobTimeFreeze: res.jobTimeFreeze ?? false,
          jobDocFreeze: res.jobDocFreeze ?? false,
          gCreatedDateSs: res.createdDate
            ? oppToExcelDateTime(res.createdDate)
            : null, //for Shipment Status Report
          gReqBookingApprovalDateSs: res.reqBookingApprovalDate
            ? oppToExcelDateTime(res.reqBookingApprovalDate)
            : null, //for Shipment Status Report
          gFirstArrivalTimeSs: res.firstArrivalTime
            ? oppToExcelDateTime(res.firstArrivalTime)
            : null, //for Shipment Status Report
          gFirstDepatureTimeSs: res.firstDepartureTime
            ? oppToExcelDateTime(res.firstDepartureTime)
            : null, //for Shipment Status Report
          gLastArrivalTimeSs: res.lastArrivalTime
            ? oppToExcelDateTime(res.lastArrivalTime)
            : null, //for Shipment Status Report
          gLastDepatureTimeSs: res.lastDepartureTime
            ? oppToExcelDateTime(res.lastDepartureTime)
            : null, //for Shipment Status Report
          reqDeliveryCutOffTimeSs: res.reqDeliveryCutOffTime
            ? oppToExcelDateTime(res.reqDeliveryCutOffTime)
            : null, //for Shipment Status Report
          reqIsBackhaul: res.reqIsBackhaul ?? false,
          backhaulRequestCode: res.backhaulRequestCode ?? null,
          backhaulRequestId: res.backhaulRequestId ?? null,
          focStatus: res.focStatus,
          pickupDemurrageReason: res.pickupDemurrageReason ? res.pickupDemurrageReason : "",
        };

        mappedResponses.push(mappedRes);
      }

      console.log("mappedResponses", mappedResponses);

      setSearchResults(mappedResponses);
    } else {
      toast.error("Error fetching data", {
        autoClose: 5000,
        containerId: "Main",
      });
    }
    setActiveAccordion(false);
    setSearchResultsPending(false);
  };

  console.log("searchResults", searchResults);

  const handleReset = () => {
    setSearchResults([]);
  };

  const handleAccordionClick = (index) => {
    setActiveAccordion(index === activeAccordion ? null : index);
  };

  const modalRef = useRef();
  const modalRef2 = useRef();

  const update = async ({
    currentRes,
    key,
    value,
    rowData,
    updateState = true,
  }) => {
    try {
      await updateJob({
        jobId: currentRes.jobId,
        updateMap: {
          [key]: value,
        },
      });
      if (updateState) {
        setSearchResults((prevState) =>
          prevState.map((res) => {
            if (res.jobCode === rowData.jobCode) {
              return {
                ...res,
                [key]: value,
              };
            }
            return res;
          }),
        );
      }
      toast.success(
        `${key === eventKeys.JOB_REF ? "Reference" : key} updated successfully!`,
        { autoClose: 5000, containerId: "Main" },
      );
    } catch (e) {
      console.error(e);

      const errorMessage = e?.response?.data || "Update Error";

      toast.error(errorMessage, {
        autoClose: 5000,
        containerId: "Main",
      });
    }
  };

  const handleSelectionChanged = useCallback(() => {
    if (gridRef.current) {
      const selectedData = gridRef.current.getSelectedData();
      setSelectedRows(selectedData);

      const adHocEvInvoiceEnableList = adHocEvInvEnableListRef.current
        ? adHocEvInvEnableListRef.current
        : [];
      const releaseNotesEnableList = releaseNoteEnableListRef.current
        ? releaseNoteEnableListRef.current
        : [];
      const focList = focEnabledListRef.current
        ? focEnabledListRef.current
        : [];
      const locationBasedReleaseNotesEnableList =
        locationBasedReleaseNoteEnableListRef.current ?? [];
      const kmCalcActionIds = kmCalcActionIdsRef.current;
      const operationCompleteSta = operationCompleteStatusRef.current;

      if (selectedData.length <= 0) {
        setShowActionMenu(false);
        setActionMenuOptions([]);
        return;
      }

      if (selectedData.length === 1) {
        const singleRow = selectedData[0];
        console.log("single Row", singleRow);
        // Single Row
        const menuList = [];

        if (focRevPermission && focList?.includes(singleRow?.staId)) {
          menuList.push({
            key: 26,
            text: "FOC Revenue",
            value: 26,
          });
        }

        if (focCostPermission && focList?.includes(singleRow?.staId)) {
          menuList.push({
            key: 27,
            text: "FOC Cost",
            value: 27,
          });
        }

        if (requestCopyPermission) {
          menuList.push({
            key: 9,
            text: "Request Copy",
            value: 9,
          });
        }
        const allowedIds = kmCalcActionIdsRef.current || [];
        const isAllowed = allowedIds
          .map(Number)
          .includes(Number(singleRow.staId));

        if (isAllowed && autoDistancePermission && singleRow.hasGpsCalc) {
          menuList.push({
            key: 15,
            text: "Auto Distance Calculate",
            value: 15,
          });
        }
        if (vehicleLogPermission) {
          menuList.push({
            key: 11,
            text: "Vehicle Log",
            value: 11,
          });
        }

        if (shipmentStatusReportPermission) {
          menuList.push({
            key: 12,
            text: "Shipment Status",
            value: 12,
          });
        }

        if (flagLogPermission) {
          if (singleRow.latestFlag !== null) {
            menuList.push({
              key: 10,
              text: "Flag Log",
              value: 10,
            });
          }
        }

        if (adHocEvInvoiceEnableList?.includes(singleRow?.staId)) {
          menuList.push({
            key: 3,
            text: "Add EV",
            value: mode.EV,
          });
          menuList.push({
            key: 4,
            text: "Add INV",
            value: mode.INV,
          });
        }
        console.log(releaseNotesEnableList);
        if (releaseNotesEnableList.includes(singleRow.staId)) {
          menuList.push({ key: 1, text: "Release Note", value: 1 });
        }
        if (locationBasedReleaseNotesEnableList?.includes(singleRow?.staId)) {
          menuList.push({
            key: 13,
            text: "Location Based Release Note",
            value: 13,
          });
        }
        if(liveTrackOptionPermission){
         menuList.push({
           key: 28,
           text: "Live Track Link",
           value: 28,
           });
          }

        if (singleRow?.staId < operationCompleteSta) {
          setActionMenuOptions(menuList);
          setShowActionMenu(menuList.length > 0);
          return;
        }
        if (singleRow?.hasChargeableKm && singleRow.confStatus === 2) {
          menuList.push({
            key: 2,
            text: "Job Confirm",
            value: 2,
          });
        }

        if (
          freezeDocUploadPermission &&
          !singleRow?.jobDocFreeze &&
          singleRow?.hasKmActual
        ) {
          menuList.push({
            key: 14,
            text: "Freeze Doc Upload",
            value: 14,
          });
        }

        if (
          unFreezeDocUploadPermission &&
          singleRow?.jobDocFreeze &&
          singleRow?.hasKmActual
        ) {
          menuList.push({
            key: 24,
            text: "UnFreeze Doc Upload",
            value: 24,
          });
        }

        if (
          freezeTimeUpdatePermission &&
          !singleRow?.jobTimeFreeze &&
          singleRow?.hasKmActual
        ) {
          menuList.push({
            key: 16,
            text: "Freeze Time Update",
            value: 16,
          });
        }

        if (
          unFreezeTimeUpdatePermission &&
          singleRow?.jobTimeFreeze &&
          singleRow?.hasKmActual
        ) {
          menuList.push({
            key: 25,
            text: "UnFreeze Time Update",
            value: 25,
          });
        }


        setActionMenuOptions(menuList);
        setShowActionMenu(menuList.length > 0);
      } else {
        // Multiple rows
        const menuList = [];

        const focEnabledRows = selectedData?.filter((row) =>
          focList?.includes(row?.staId),
        );

        if (focEnabledRows?.length > 0) {
          if (focCostPermission) {
            menuList.push({
              key: 27,
              text: "FOC Cost",
              value: 27,
            });
          }

          if (focRevPermission) {
            menuList.push({
              key: 26,
              text: "FOC Revenue",
              value: 26,
            });
          }
        }

        const invalidStatusRows = selectedData.filter(
          (row) => row.confStatus !== 2 || !row.hasChargeableKm,
        );

        if (vehicleLogPermission) {
          menuList.push({
            key: 11,
            text: "Vehicle Log",
            value: 11,
          });
        }

        if (shipmentStatusReportPermission) {
          menuList.push({
            key: 12,
            text: "Shipment Status",
            value: 12,
          });
        }

        if (invalidStatusRows.length === 0) {
          menuList.push({
            key: 2,
            text: "Job Confirm",
            value: 2,
          });
        }

        const atLeastOneFlag = selectedData.some(
          (row) => row.latestFlag !== null,
        );
        if (flagLogPermission) {
          if (atLeastOneFlag) {
            menuList.push({
              key: 10,
              text: "Flag Log",
              value: 10,
            });
          }
        }

        const freezeDocUpload = selectedData.filter(
          (row) => !row.jobDocFreeze && row.hasKmActual,
        );

        const freezeTimeUpdate = selectedData.filter(
          (row) => !row.jobTimeFreeze && row.hasKmActual,
        );

        if (freezeDocUploadPermission && freezeDocUpload.length > 0) {
          menuList.push({
            key: 14,
            text: "Freeze Doc Upload",
            value: 14,
          });
        }

        if (freezeTimeUpdatePermission && freezeTimeUpdate.length > 0) {
          menuList.push({
            key: 16,
            text: "Freeze Time Update",
            value: 16,
          });
        }

        const unFreezeDocUpload = selectedData.filter(
          (row) => row.jobDocFreeze === true && row.hasKmActual,
        );

        const unFreezeTimeUpdate = selectedData.filter(
          (row) => row.jobTimeFreeze === true && row.hasKmActual,
        );

        if (unFreezeDocUploadPermission && unFreezeDocUpload.length > 0) {
          menuList.push({
            key: 24,
            text: "UnFreeze Doc Upload",
            value: 24,
          });
        }

        if (unFreezeTimeUpdatePermission && unFreezeTimeUpdate.length > 0) {
          menuList.push({
            key: 25,
            text: "UnFreeze Time Update",
            value: 25,
          });
        }

        setActionMenuOptions(menuList);
      }
      setShowActionMenu(true);
    }
  }, []);

  const openTemplateModal = () => {
    modalRef2.current?.showModal();
  };

  const closeTemplateModal = () => {
    modalRef2.current?.close();
  };

  const pickFromTemplate = (e) => {
    e.preventDefault();
    setDrawerContent("EV_INV_CREATION");
    closeTemplateModal();
    setIsDrawerOpen(true);
  };

  const handleDropdownChange = async (key, value, rowData) => {
    const currentRes = searchResults.find(
      (res) => res.jobCode === rowData.jobCode,
    );

    let oldValue = currentRes[key];

    setSearchResults((prevState) =>
      prevState.map((res) => {
        if (res.jobCode === rowData.jobCode) {
          return {
            ...res,
            [key]: value,
          };
        }
        return res;
      }),
    );

    setUpdateState({
      currentRes,
      key,
      value,
      rowData,
      oldValue,
      updateOnCancel: true,
      updateState: false,
    });
    setAlertOpen(true);
  };

  console.log("selectedData", selectedRows);

  const downloadFlagLog = async (selectedJobIds) => {
    try {
      setIsDownloading(true);
      const responseMap = await flagLogHistory({ jobIds: selectedJobIds });

      if (!responseMap || Object.keys(responseMap).length === 0) {
        setIsDownloading(false);
        toast.warn("No Flags Found", { autoClose: 5000, containerId: "Main" });
        return;
      }

      const headerMap = {
        job: "Job No",
        request: "Request",
        jfhCode: "Flag Code",
        attendBy: "Attend By",
        flagType: "Flag Type",
        jfhRemarks: "Flag Remark",
        jfhTime: "Flag Time",
        flagBy: "Flag By",
        acceptedTime: "Accepted Time",
        acceptedBy: "Accepted By",
        lastModifiedDate: "Resolve Time",
        lastModifiedBy: "Resolve By",
        jfhResolvedRemark: "Resolved Remark",
      };

      const flatRows = [];

      // The flag log view does not always populate the "resolved" label, so
      // match on the numeric flag_resolve_status code for "Accepted" instead
      const { flag_resolve_status: resolveStatusOptions = [] } =
        await getBulkDropdownData([{ key: "flag_resolve_status" }]);
      const acceptedCode = resolveStatusOptions.find(
        (opt) => (opt.text ?? "").toString().trim().toLowerCase() === "accepted",
      )?.value;

      const isAccepted = (item) =>
        (acceptedCode != null &&
          item?.jfhResolved != null &&
          Number(item.jfhResolved) === Number(acceptedCode)) ||
        (item?.resolved ?? "").toString().trim().toLowerCase() === "accepted";

      for (const [jobId, items] of Object.entries(responseMap)) {
        if (!Array.isArray(items) || items.length === 0) continue;

        // Find the history entry where each flag transitioned into "Accepted"
        // (the most recent acceptance if it was accepted more than once)
        const acceptedByCode = {};
        [...items]
          .sort(
            (a, b) =>
              new Date(a.lastModifiedDate || a.jfhTime) -
              new Date(b.lastModifiedDate || b.jfhTime),
          )
          .forEach((item) => {
            const key = item.jfhCode;
            const entry = acceptedByCode[key] ?? { wasAccepted: false };
            const nowAccepted = isAccepted(item);
            if (nowAccepted && !entry.wasAccepted) {
              entry.acceptedTime = item.lastModifiedDate ?? item.jfhTime;
              entry.acceptedBy = item.lastModifiedBy ?? item.createdBy ?? "";
            }
            entry.wasAccepted = nowAccepted;
            acceptedByCode[key] = entry;
          });

        // Deduplicate by jfhCode — keep the latest entry for each unique flag
        const uniqueFlags = Object.values(
          items.reduce((acc, item) => {
            const key = item.jfhCode;
            if (!acc[key]) {
              acc[key] = item;
            } else {
              const existing = new Date(
                acc[key].lastModifiedDate || acc[key].jfhTime,
              );
              const incoming = new Date(item.lastModifiedDate || item.jfhTime);
              if (incoming > existing) {
                acc[key] = item;
              }
            }
            return acc;
          }, {}),
        );

        uniqueFlags.forEach((item, idx) => {
          flatRows.push({
            job: idx === 0 ? item.job : "",
            request: idx === 0 ? item.request ?? "" : "",
            jfhCode: item.jfhCode ?? "",
            attendBy: item.createdBy ?? "",
            flagType: item.flagType ?? "",
            jfhRemarks: item.jfhRemarks ?? "",
            jfhTime: item.jfhTime ? formatCsvDate(item.jfhTime) : "N/A",
            flagBy: item.createdBy ?? "",
            acceptedTime: acceptedByCode[item.jfhCode]?.acceptedTime
              ? formatCsvDate(acceptedByCode[item.jfhCode].acceptedTime)
              : "N/A",
            acceptedBy: acceptedByCode[item.jfhCode]?.acceptedBy ?? "",
            lastModifiedDate: item.lastModifiedDate
              ? formatCsvDate(item.lastModifiedDate)
              : "N/A",
            lastModifiedBy: item.lastModifiedBy ?? "",
            jfhResolvedRemark: item.jfhResolvedRemark ?? "",
          });
        });
      }

      if (flatRows.length === 0) {
        toast.warn("No Flags Found", { autoClose: 5000, containerId: "Main" });
        setIsDownloading(false);
        return;
      }

      const fields = Object.keys(headerMap);

      const csvLines = [];
      csvLines.push(fields.map((f) => headerMap[f]).join(","));

      for (const row of flatRows) {
        const line = fields.map((f) => {
          let cell = row[f] ?? "";
          cell = cell.toString().replace(/"/g, '""');
          return `"${cell}"`;
        });
        csvLines.push(line.join(","));
      }

      const blob = new Blob([csvLines.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "flag_log.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleVehicleAssignmentHistoryDownload = async (selectedJobIds) => {
    try {
      setIsDownloading(true);

      const dataMap = await vehicleAssignmentHistory({
        jobIds: selectedJobIds,
      });

      if (!dataMap || Object.keys(dataMap).length === 0) {
        toast.warn("No assignment history found", {
          autoClose: 5000,
          containerId: "Main",
        });
        setIsDownloading(false);
        return;
      }

      const vehicleHeaderMap = {
        job: "Job Code",
        code: "Veh Code",
        vehicle: "Vehicle",
        transporter: "Transporter",
        driver: "Driver",
        helper: "Helper",
        sec_driver: "Secondary Driver",
        sec_helper: "Secondary Helper",
        vehicle_change: "Vehicle Change",
        vehicle_change_comment: "Change Comment",
        vehicle_change_by: "Changed By",
        vehicle_change_time: "Change Time",
        // status: "Status",
        // active: "Active",
        created_by: "Created By",
        created_date: "Created On",
        last_modified_by: "Last Edited By",
        last_modified_date: "Last Edited On",
      };

      const containerHeaderMap = {
        job: "Job Code",
        jchCode: "Container History Code",
        action: "Action",
        request: "Request ID",
        containerType: "Container Type",
        trailer: "Trailer Type",
        trailerNumber: "Trailer Number",
        trailerType: "Trailer",
        containerNumber: "Container Number",
        sealNumber1: "Seal Number 1",
        sealNumber2: "Seal Number 2",
        sealNumber3: "Seal Number 3",
        sealNumber4: "Seal Number 4",
        // status: "Status",
        // active: "Active",
        createdBy: "Created By",
        createdDate: "Created On",
        lastModifiedBy: "Last Edited By",
        lastModifiedDate: "Last Edited On",
      };

      const vehicleRows = [];
      const containerRows = [];

      for (const jobId of Object.keys(dataMap)) {
        const jobData = dataMap[jobId];

        (jobData.vehicleAssignmentLogResponseList || []).forEach((r, idx) => {
          vehicleRows.push({
            ...r,
            vehicle_change_time: r.vehicle_change_time
              ? formatCsvDate(r.vehicle_change_time)
              : "N/A",
            created_date: r.created_date
              ? formatCsvDate(r.created_date)
              : "N/A",
            last_modified_date: r.last_modified_date
              ? formatCsvDate(r.last_modified_date)
              : "N/A",
            job: idx === 0 ? r.job : "",
          });
        });

        (jobData.containerAssignmentLogResponseList || []).forEach((r, idx) => {
          containerRows.push({
            ...r,
            createdDate: r.createdDate ? formatCsvDate(r.createdDate) : "N/A",
            lastModifiedDate: r.lastModifiedDate
              ? formatCsvDate(r.lastModifiedDate)
              : "N/A",
            job: idx === 0 ? r.job : "",
          });
        });
      }

      const csvLines = [];

      // Section 1: Vehicle Assignments
      csvLines.push("Vehicle Assignment History");
      const vehicleFields = [
        "job",
        ...Object.keys(vehicleHeaderMap).filter((k) => k !== "job"),
      ];
      csvLines.push(
        vehicleFields.map((f) => vehicleHeaderMap[f] || f).join(","),
      );
      vehicleRows.forEach((row) => {
        const line = vehicleFields
          .map((f) => `"${(row[f] ?? "").toString().replace(/"/g, '""')}"`)
          .join(",");
        csvLines.push(line);
      });

      // Blank line separator
      csvLines.push("");

      // Section 2: Container Assignments
      csvLines.push("Container Assignment History");
      const containerFields = [
        "job",
        ...Object.keys(containerHeaderMap).filter((k) => k !== "job"),
      ];
      csvLines.push(
        containerFields.map((f) => containerHeaderMap[f] || f).join(","),
      );
      containerRows.forEach((row) => {
        const line = containerFields
          .map((f) => `"${(row[f] ?? "").toString().replace(/"/g, '""')}"`)
          .join(",");
        csvLines.push(line);
      });

      // Create and download CSV
      const blob = new Blob([csvLines.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `assignment_history.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setIsDownloading(false);
    }
  };

  const toExcelNumber = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n =
      typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
  };

  const handleShipmentStatusDownload = async (selectedJobIds) => {
    try {
      setIsSsrDownloading(true);

      // Fetch the data
      const dataMap = await shipmentStatus({ jobIds: selectedJobIds });
      if (!dataMap || Object.keys(dataMap).length === 0) {
        setIsSsrDownloading(false);
        toast.success("No shipment status found", {
          autoClose: 5000,
          containerId: "Main",
        });
        return;
      }

      //Flatten the data
      const flatRows = [];
      const records = Object.values(dataMap)[0] || [];
      records?.forEach((rec, idx) => {
        const normalized = Object.fromEntries(
          Object.entries(rec).map(([k, v]) => {
            if (typeof v === "string" && /\d{4}-\d{2}-\d{2}T/.test(v)) {
              return [k, oppToExcelDateTime(v)];
            }
            return [k, v];
          }),
        );

        flatRows.push(normalized);
      });

      if (flatRows.length === 0) {
        setIsSsrDownloading(false);
        toast.warn("No shipment status found", {
          autoClose: 5000,
          containerId: "Main",
        });
        return;
      }

      // Manual header mapping
      const headerMap = {
        reqNo: "Request Id",
        jobNo: "Job No",
        createdDate: "Created Date",
        createdBy: "Created By",
        approvedBy: "Approved By",
        approvedDate: "Approved Date",
        vehicle: "Vehicle",
        driver: "Driver",
        vehicleAssignedBy: "Attended By",
        // vehicleAssignedDate: "Attended Date",
        pickupArrivalTime: "Arrived at Pickup",
        pickupDepartureTime: "Departed from Pickup",
        deliveryArrivalTime: "Arrived at Delivery",
        deliveryDepartureTime: "Departure from Delivery",
        lateBooking: "Late Booking",
        placementStatus: "Placement Status",
        ontimePlacement: "On Time Placement",
        ontimeDelivery: "On Time Delivery",
        samedayDelivery: "Same Day Delivery",
        spentPp: "Spent at PP",
        spentDp: "Spent at DP",
        transitTime: "Transit Time",
        ppPercentage: "Spent at PP%",
        dpPercentage: "Spent at DP%",
        transitPercentage: "Transit Time%",
        shipper: "Shipper/Plant",
        pickupLocation: "Pickup",
        requestedArrivalTime: "Requested Arrival Time",
        plantDispatchedTime: "Planned Dispatched Time",
        viaLocations: "Via Locations",
        deliveryLocation: "Delivery",
        requestedDeliveryTime: "Requested Delivery Time",
        operationType: "Shipment Type",
        consignmentType: "Consignment Type",
        consignee: "Consignee",
        reqCbm: "CBM/Volume",
        vhtType: "Vehicle Type",
        cotType: "Container Type",
        creatorRemark: "Creator Remarks",
        chargeableKm: "Chargeable KM",
        cbm: "CBM",
        packages: "Packages",
        containerNo: "Container Number",
        contactNo: "Contact Number",
        department: "Department",
        jobPoNo: "Reference PO No",
        sealNumbers: "Security/Serial Number",
        remarks: "Remarks",
        actualCbm: "Actual CBM",
        jobAod: "DAN/AOD No",
        buyerName: "Buyer Name",
        deliveryCutoff: "Cut Off Date",
        cbf: "CBF",
        actualKm: "Trip KM",
        voyageNo: "Voyage No",
        vesselName: "Vessel Name",
        otherRequests: "Other Requests",
        vendorInvoiceNo: "Vendor Invoice No",
        customerName: "Customer",
        billingCustomer: "Billing Customer",
        totalCharge: "Total Charge",
        demmurageReason: "Demurrage Reason",
        invoiceNo: "Invoice No",
        invoiceDate: "Invoice Date",
        requestPoNo: "PO No",

        consignee: "Consignee",
        cargoInHand: "Cargo In Hand KM",
        jobSeal1: "Security/Seal No",
        jobSeal2: "Second Seal No",
        // jobSeal3: "Job Seal 3",
        returnTheJob: "Return The Job",
        blCutoff: "BL Cutoff",
        vehicleRequestedTime: "Vehicle Requested Time By Plant",
        commitJob: "Commitment Job",
        currentStatus: "Current Status",
        fuelAdjustmentFee: "Fuel Adjustment Fee",
        droppingPointCharges: "Dropping Point Charges",
        tallyFee: "Tally Fee",
        loadUnload: "Load/Unload",
        lashingCharges: "Lashing Charges",
        boiCharge: "BOI Charge",
        transportCharges: "Transport Charge",
        managementFee: "Management Fee",
        monitoringFee: "Monitoring Fee",
        nightParkCharge: "Night Park",
        other: "Other Charge",
        highwayCharges: "Highway Charges",
        unloadingCharges: "Unloading Charges",
        demurrageKm: "Demurrage",
        discount: "Discount",
        containerCharges: "Container Charges",
        weighingCharges: "Weighing Charges",
        loadingCharges: "Loading Charges",
        demurrageKmHours: "Demurrage Hours",
        // totalRevenue: "Total Revenue",
      };

      const DATETIME_FIELDS = new Set([
        "createdDate",
        "approvedDate",
        "pickupArrivalTime",
        "pickupDepartureTime",
        "deliveryArrivalTime",
        "deliveryDepartureTime",
        "requestedArrivalTime",
        "plantDispatchedTime",
        "requestedDeliveryTime",
        "deliveryCutoff",
        "invoiceDate",
        "blCutoff",
        "vehicleRequestedTime",
      ]);

      const EXCEL_DATETIME_FMT = "mm-dd-yyyy hh:mm";

      const CHARGE_FIELDS = new Set([
        "totalCharge",
        "fuelAdjustmentFee",
        "droppingPointCharges",
        "tallyFee",
        "loadUnload",
        "lashingCharges",
        "boiCharge",
        "transportCharges",
        "managementFee",
        "monitoringFee",
        "nightParkCharge",
        "other",
        "highwayCharges",
        "unloadingCharges",
        "demurrageKm",
        "discount",
        "containerCharges",
        "weighingCharges",
        "loadingCharges",
      ]);
      const EXCEL_NUMBER_FMT = "0.00";

      // Create Excel workbook
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Shipment Status");

      // Define columns
      const fields = Object.keys(headerMap);
      sheet.columns = fields.map((f) => ({
        header: headerMap[f],
        key: f,
        width: 20,
      }));

      //set datetime formatting AFTER columns exist
      DATETIME_FIELDS.forEach((key) => {
        sheet.getColumn(key).numFmt = EXCEL_DATETIME_FMT;
      });

      CHARGE_FIELDS.forEach((key) => {
        sheet.getColumn(key).numFmt = EXCEL_NUMBER_FMT;
      });

      flatRows.forEach((row) => {
        const cleanRow = {};
        fields.forEach((f) => {
          if (DATETIME_FIELDS.has(f)) {
            cleanRow[f] = row[f] ?? null; // Date or null
          } else if (CHARGE_FIELDS.has(f)) {
            cleanRow[f] = toExcelNumber(row[f]); // Number or null
          } else {
            cleanRow[f] = row[f] ?? "";
          }
        });
        sheet.addRow(cleanRow);
      });

      //Auto-fit column widths here
      sheet.columns.forEach((column) => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const columnLength = cell.value ? cell.value.toString().length : 0;
          if (columnLength > maxLength) {
            maxLength = columnLength;
          }
        });
        column.width = maxLength < 10 ? 10 : maxLength + 2;
      });

      // //Apply header styling (highlight + border)
      // sheet.getRow(1).eachCell((cell) => {
      //   cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      //   cell.fill = {
      //     type: "pattern",
      //     pattern: "solid",
      //     fgColor: { argb: "FF2F75B5" }, // blue header background
      //   };
      //   cell.border = {
      //     top: { style: "thin", color: { argb: "FF000000" } },
      //     left: { style: "thin", color: { argb: "FF000000" } },
      //     bottom: { style: "thin", color: { argb: "FF000000" } },
      //     right: { style: "thin", color: { argb: "FF000000" } },
      //   };
      // });
      //
      // //Apply borders for all data cells
      // sheet.eachRow((row, rowNumber) => {
      //   row.eachCell((cell) => {
      //     cell.border = {
      //       top: { style: "thin", color: { argb: "FF000000" } },
      //       left: { style: "thin", color: { argb: "FF000000" } },
      //       bottom: { style: "thin", color: { argb: "FF000000" } },
      //       right: { style: "thin", color: { argb: "FF000000" } },
      //     };
      //   });
      // });

      // Header styling
      sheet.getRow(1).eachCell({ includeEmpty: true }, (cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF2F75B5" },
        };
      });

      // ✅ FIX: borders for ALL cells including empty ones
      const THIN_BORDER = {
        top: { style: "thin", color: { argb: "FF000000" } },
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };

      const lastRow = sheet.rowCount; // includes header + data rows
      const lastCol = sheet.columnCount; // all defined columns

      for (let r = 1; r <= lastRow; r++) {
        const row = sheet.getRow(r);
        for (let c = 1; c <= lastCol; c++) {
          row.getCell(c).border = THIN_BORDER; // forces empty cell creation
        }
      }

      //Export and trigger download (your original method)
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "shipment_status.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSsrDownloading(false);
    }
  };

  const handleShipmentStatusPerformancedReportDownload = async (
    selectedJobIds,
  ) => {
    try {
      setIsSsrDownloading(true);

      // Fetch the data
      const dataMap = await shipmentStatusRemainingData({
        jobIds: selectedJobIds,
      });
      console.log("selectedDataMap", dataMap);
      if (!dataMap || dataMap?.length === 0) {
        setIsSsrDownloading(false);
        toast.success("No shipment status found", {
          autoClose: 5000,
          containerId: "Main",
        });
        return;
      }

      // index selectedData by jobId
      const selectedByJobId = new Map();
      for (const s of selectedRows) {
        selectedByJobId.set(s.jobId, s);
      }

      // iterate dataMap and merge only matches
      const flatRows = [];
      for (const d of dataMap) {
        const s = selectedByJobId.get(d.jobId);
        if (s) {
          flatRows.push({
            reqNo: s.gReqCode,
            jobNo: s.gJobCode,
            createdDate: s.gCreatedDateSs, //
            createdBy: s.gCreatedBy,
            approvedBy: s.gReqBookingApprovedBy,
            approvedDate: s.gReqBookingApprovalDateSs, //
            vehicle: s.vehVehicleno,
            driver: s.gDriverName,
            vehicleAssignedBy: s.vehicleAssignedBy,
            pickupArrivalTime: s.gFirstArrivalTimeSs, //
            pickupDepartureTime: s.gFirstDepatureTimeSs, //
            deliveryArrivalTime: s.gLastArrivalTimeSs, //
            deliveryDepartureTime: s.gLastDepatureTimeSs, //
            lateBooking: s.reqLateBooking,
            placementStatus: d.placementStatus,
            ontimePlacement: d.ontimePlacement,
            ontimeDelivery: d.ontimeDelivery,
            samedayDelivery: d.samedayDelivery,
            spentPp: d.spentPp,
            spentDp: d.spentDp,
            transitTime: d.transitTime,
            ppPercentage: d.ppPercentage,
            dpPercentage: d.dpPercentage,
            transitPercentage: d.transitPercentage,
            shipper: s.gReqPlant,
            pickupLocation: s.startLocation,
            requestedArrivalTime: oppToExcelDateTime(d.requestedArrivalTime), //
            plantDispatchedTime: oppToExcelDateTime(d.plantDispatchedTime), //
            viaLocations: d.viaLocations,
            deliveryLocation: s.endLocation,
            requestedDeliveryTime: oppToExcelDateTime(d.requestedDeliveryTime), //
            operationType: s.shipmentType,
            consignmentType: s.consignmentType,
            reqCbm: s.gReqCbm,
            vhtType: s.gVhtType,
            cotType: d.cotType,
            creatorRemark: s.creatorRemark,
            chargeableKm: s.gChargeableKm,
            cbm: s.gReqCbm,
            packages: d.packages,
            containerNo: s.containerNo,
            contactNo: s.reqContactNo,
            department: d.department,
            jobPoNo: s.reqPoNo,
            sealNumbers:
              `${s.jobSeal1 ?? ""} ${s.jobSeal2 ?? ""} ${s.jobSeal3 ?? ""}`.trim(),
            remarks: s.creatorRemark,
            actualCbm: s.gReqCbm,
            jobAod: s.jobAod,
            buyerName: d.buyerName,
            deliveryCutoff: s.reqDeliveryCutOffTimeSs, //
            cbf: s.gReqCbm,
            // cbf: d.cbf,
            actualKm: s.actualKm,
            voyageNo: d.voyageNo,
            vesselName: d.vesselName,
            otherRequests: s.otherCharges,
            vendorInvoiceNo: s.reqInvoiceRef,
            customerName: s.gReqCustomer,
            billingCustomer: s.gReqBillingCustomer,
            totalCharge: s.totalRev,
            demmurageReason: d.demmurageReason,
            invoiceNo: d.invoiceNo,
            invoiceDate: oppToExcelDateTime(d.invoiceDate), //
            requestPoNo: s.reqPoNo,
            consignee: s.consignee,
            cargoInHand: s.actualKm,
            jobSeal1: s.jobSeal1,
            jobSeal2: s.jobSeal2,
            returnTheJob: d.returnTheJob,
            blCutoff: s.reqDeliveryCutOffTimeSs,
            // blCutoff: d.blCutoff, //empty
            vehicleRequestedTime: oppToExcelDateTime(d.vehicleRequestedTime), //
            commitJob: d.commitJob,
            currentStatus: s.gStaName,
            //below are set of charges.
            fuelAdjustmentFee: s.fuelAdjRev,
            droppingPointCharges: s.droppingPointRev,
            tallyFee: s.tallyRev,
            loadUnload: s.loadUnloadRev,
            lashingCharges: s.lashingRev,
            boiCharge: s.boiRev,
            transportCharges: s.transportRev,
            managementFee: s.managementRev,
            monitoringFee: s.monitoringRev,
            nightParkCharge: s.nightParkRev,
            other: s.otherRev,
            highwayCharges: s.highwayRev,
            unloadingCharges: s.unloadingRev,
            demurrageKm: s.demurrageRev,
            discount: s.discountRev,
            containerCharges: s.containerRev,
            weighingCharges: s.weighingRev,
            loadingCharges: s.loadingRev,
            demurrageKmHours: s.demmurrageKmHours,
          });
        }
      }

      if (flatRows?.length === 0) {
        setIsSsrDownloading(false);
        toast.warn("No shipment status found", {
          autoClose: 5000,
          containerId: "Main",
        });
        return;
      }

      const DATETIME_FIELDS = new Set([
        "createdDate",
        "approvedDate",
        "pickupArrivalTime",
        "pickupDepartureTime",
        "deliveryArrivalTime",
        "deliveryDepartureTime",
        "requestedArrivalTime",
        "plantDispatchedTime",
        "requestedDeliveryTime",
        "deliveryCutoff",
        "invoiceDate",
        "blCutoff",
        "vehicleRequestedTime",
      ]);

      const EXCEL_DATETIME_FMT = "mm-dd-yyyy hh:mm";

      const CHARGE_FIELDS = new Set([
        "totalCharge",
        "fuelAdjustmentFee",
        "droppingPointCharges",
        "tallyFee",
        "loadUnload",
        "lashingCharges",
        "boiCharge",
        "transportCharges",
        "managementFee",
        "monitoringFee",
        "nightParkCharge",
        "other",
        "highwayCharges",
        "unloadingCharges",
        "demurrageKm",
        "discount",
        "containerCharges",
        "weighingCharges",
        "loadingCharges",
      ]);
      const EXCEL_NUMBER_FMT = "0.00";

      // Manual header mapping
      const headerMap = {
        reqNo: "Request Id",
        jobNo: "Job No",
        createdDate: "Created Date",
        createdBy: "Created By",
        approvedBy: "Approved By",
        approvedDate: "Approved Date",
        vehicle: "Vehicle",
        driver: "Driver",
        vehicleAssignedBy: "Attended By",
        pickupArrivalTime: "Arrived at Pickup",
        pickupDepartureTime: "Departed from Pickup",
        deliveryArrivalTime: "Arrived at Delivery",
        deliveryDepartureTime: "Departure from Delivery",
        lateBooking: "Late Booking",
        placementStatus: "Placement Status",
        ontimePlacement: "On Time Placement",
        ontimeDelivery: "On Time Delivery",
        samedayDelivery: "Same Day Delivery",
        spentPp: "Spent at PP",
        spentDp: "Spent at DP",
        transitTime: "Transit Time",
        ppPercentage: "Spent at PP%",
        dpPercentage: "Spent at DP%",
        transitPercentage: "Transit Time%",
        shipper: "Shipper/Plant",
        pickupLocation: "Pickup",
        requestedArrivalTime: "Requested Arrival Time",
        plantDispatchedTime: "Planned Dispatched Time",
        viaLocations: "Via Locations",
        deliveryLocation: "Delivery",
        requestedDeliveryTime: "Requested Delivery Time",
        operationType: "Shipment Type",
        consignmentType: "Consignment Type",
        reqCbm: "CBM/Volume",
        vhtType: "Vehicle Type",
        cotType: "Container Type",
        creatorRemark: "Creator Remarks",
        chargeableKm: "Chargeable KM",
        cbm: "CBM",
        packages: "Packages",
        containerNo: "Container Number",
        contactNo: "Contact Number",
        department: "Department",
        jobPoNo: "Reference PO No",
        sealNumbers: "Security/Serial Number",
        remarks: "Remarks",
        actualCbm: "Actual CBM",
        jobAod: "DAN/AOD No",
        buyerName: "Buyer Name",
        deliveryCutoff: "Cut Off Date",
        cbf: "CBF",
        actualKm: "Trip KM",
        voyageNo: "Voyage No",
        vesselName: "Vessel Name",
        otherRequests: "Other Requests",
        vendorInvoiceNo: "Vendor Invoice No",
        customerName: "Customer",
        billingCustomer: "Billing Customer",
        totalCharge: "Total Charge",
        demmurageReason: "Demurrage Reason",
        invoiceNo: "Invoice No",
        invoiceDate: "Invoice Date",
        requestPoNo: "PO No",
        consignee: "Consignee",
        cargoInHand: "Cargo In Hand KM",
        jobSeal1: "Security/Seal No",
        jobSeal2: "Second Seal No",
        returnTheJob: "Return The Job",
        blCutoff: "BL Cutoff",
        vehicleRequestedTime: "Vehicle Requested Time By Plant",
        commitJob: "Commitment Job",
        currentStatus: "Current Status",
        fuelAdjustmentFee: "Fuel Adjustment Fee",
        droppingPointCharges: "Dropping Point Charges",
        tallyFee: "Tally Fee",
        loadUnload: "Load/Unload",
        lashingCharges: "Lashing Charges",
        boiCharge: "BOI Charge",
        transportCharges: "Transport Charge",
        managementFee: "Management Fee",
        monitoringFee: "Monitoring Fee",
        nightParkCharge: "Night Park",
        other: "Other Charge",
        highwayCharges: "Highway Charges",
        unloadingCharges: "Unloading Charges",
        demurrageKm: "Demurrage",
        discount: "Discount",
        containerCharges: "Container Charges",
        weighingCharges: "Weighing Charges",
        loadingCharges: "Loading Charges",
        demurrageKmHours: "Demurrage Hours",
      };

      // Create Excel workbook
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Shipment Status");

      // Define columns
      const fields = Object.keys(headerMap);
      sheet.columns = fields.map((f) => ({
        header: headerMap[f],
        key: f,
        width: 20,
      }));

      //set datetime formatting AFTER columns exist
      DATETIME_FIELDS.forEach((key) => {
        sheet.getColumn(key).numFmt = EXCEL_DATETIME_FMT;
      });

      CHARGE_FIELDS.forEach((key) => {
        sheet.getColumn(key).numFmt = EXCEL_NUMBER_FMT;
      });

      flatRows.forEach((row) => {
        const cleanRow = {};
        fields.forEach((f) => {
          if (DATETIME_FIELDS.has(f)) {
            cleanRow[f] = row[f] ?? null; // Date or null
          } else if (CHARGE_FIELDS.has(f)) {
            cleanRow[f] = toExcelNumber(row[f]); // Number or null
          } else {
            cleanRow[f] = row[f] ?? "";
          }
        });
        sheet.addRow(cleanRow);
      });

      //Auto-fit column widths here
      sheet.columns.forEach((column) => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const columnLength = cell.value ? cell.value.toString().length : 0;
          if (columnLength > maxLength) {
            maxLength = columnLength;
          }
        });
        column.width = maxLength < 10 ? 10 : maxLength + 2;
      });

      //Apply header styling (highlight + border)
      // sheet.getRow(1).eachCell((cell) => {
      //   cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      //   cell.fill = {
      //     type: "pattern",
      //     pattern: "solid",
      //     fgColor: { argb: "FF2F75B5" }, // blue header background
      //   };
      //   cell.border = {
      //     top: { style: "thin", color: { argb: "FF000000" } },
      //     left: { style: "thin", color: { argb: "FF000000" } },
      //     bottom: { style: "thin", color: { argb: "FF000000" } },
      //     right: { style: "thin", color: { argb: "FF000000" } },
      //   };
      // });
      //
      // //Apply borders for all data cells
      // sheet.eachRow((row, rowNumber) => {
      //   row.eachCell((cell) => {
      //     cell.border = {
      //       top: { style: "thin", color: { argb: "FF000000" } },
      //       left: { style: "thin", color: { argb: "FF000000" } },
      //       bottom: { style: "thin", color: { argb: "FF000000" } },
      //       right: { style: "thin", color: { argb: "FF000000" } },
      //     };
      //   });
      // });

      // Header styling
      sheet.getRow(1).eachCell({ includeEmpty: true }, (cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF2F75B5" },
        };
      });

      // ✅ FIX: borders for ALL cells including empty ones
      const THIN_BORDER = {
        top: { style: "thin", color: { argb: "FF000000" } },
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };

      const lastRow = sheet.rowCount; // includes header + data rows
      const lastCol = sheet.columnCount; // all defined columns

      for (let r = 1; r <= lastRow; r++) {
        const row = sheet.getRow(r);
        for (let c = 1; c <= lastCol; c++) {
          row.getCell(c).border = THIN_BORDER; // forces empty cell creation
        }
      }

      //Export and trigger download (your original method)
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "shipment_status.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setIsSsrDownloading(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSsrDownloading(false);
    }
  };

  const openColumnConfig = () => {
    setPopUpContent(gridHeaders.COLUMN_CONFIG);
    modalRef.current?.showModal();
  };

  console.log("selectedRows", selectedRows);
  const enableFocRev = async () => {
    const focList = focEnabledListRef.current ? focEnabledListRef.current : [];
    const selectedJobIds =
      selectedRows?.length > 0
        ? selectedRows
            .filter((item) => focList?.includes(item.staId)) // First filter
            .map((item) => item.jobId) // Then map
        : [];
    if (selectedJobIds?.length > 0) {
      try {
        const response = await enableFoc({
          jobList: selectedJobIds.join(","),
          chargeType: 1,
        });
        console.log("Revenue response", response);
        focRef.current?.close();

        setUpdateNeeded(true);

        setTimeout(() => {
          toast.success(response.data || "FOC Revenue enabled successfully!", {
            autoClose: 5000,
            containerId: "Main",
          });
        }, 5000);
      } catch (e) {
        toast.error(e.response.data || "Failed to enable FOC Revenue", {
          autoClose: 5000,
          containerId: "Main",
        });
      }
    } else {
      toast.error("No jobs found to enable FOC Revenue", {
        autoClose: 5000,
        containerId: "Main",
      });
    }
  };

  const enableFocCost = async () => {
    const focList = focEnabledListRef.current ? focEnabledListRef.current : [];
    const selectedJobIds =
      selectedRows?.length > 0
        ? selectedRows
            .filter((item) => focList?.includes(item.staId)) // First filter
            .map((item) => item.jobId) // Then map
        : [];
    if (selectedJobIds?.length > 0) {
      try {
        const response = await enableFoc({
          jobList: selectedJobIds.join(","),
          chargeType: 2,
        });
        console.log("cost response", response);
        focRef.current?.close();

        setUpdateNeeded(true);
        setTimeout(() => {
          toast.success(response.data || "FOC cost enabled successfully!", {
            autoClose: 5000,
            containerId: "Main",
          });
        }, 5000);
      } catch (e) {
        toast.error(e.response.data || "Failed to enable FOC Cost", {
          autoClose: 5000,
          containerId: "Main",
        });
      }
    } else {
      toast.error("No jobs found to enable FOC Cost", {
        autoClose: 5000,
        containerId: "Main",
      });
    }
  };

  const focCostContent = (
    <div style={{ padding: "20px" }}>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <h3 style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AlertCircle color="red" size="25" />
          <p>Warning</p>{" "}
        </h3>
        <h3>
          Once FOC Cost is applied, cost charges cannot be assigned to jobs. Any
          cost charges already assigned to jobs will be removed
          <br />
          Please verify. Are you sure you want to proceed?
        </h3>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingTop: "5px",
            paddingBottom: "5px",
            gap: "5px",
          }}
        >
          <Button
            Icon={X}
            label={"Cancel"}
            onClick={() => focRef.current?.close()}
            color="red"
          />
          <Button Icon={Check} label={"Proceed"} onClick={enableFocCost} />
        </div>
      </div>
    </div>
  );

  const focRevContent = (
    <div style={{ padding: "20px" }}>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <h3 style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AlertCircle color="red" size="25" />
          <p>Warning</p>{" "}
        </h3>
        <h3>
          Once FOC Revenue is applied, revenue charges cannot be assigned to
          jobs. Any revenue charges already assigned to jobs will be removed
          <br />
          Please verify. Are you sure you want to proceed?
        </h3>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingTop: "5px",
            paddingBottom: "5px",
            gap: "5px",
          }}
        >
          <Button
            Icon={X}
            label={"Cancel"}
            onClick={() => focRef.current?.close()}
            color="red"
          />
          <Button Icon={Check} label={"Proceed"} onClick={enableFocRev} />
        </div>
      </div>
    </div>
  );

  const actionMenuHandler = async (data) => {
    switch (data?.value) {
      case 1: {
        console.log("selectedRowJobMonitor", selectedRows);
        setReleaseNoteVariant("standard");
        setShowCloseButton(true);
        setCurrentJobId(selectedRows[0].jobId);
        setCurrentJobCode(selectedRows[0].jobCode);
        setCurrentRequestId(selectedRows[0].reqId);
        setCurrentRequestCode(selectedRows[0].reqCode);
        dispatch(
          reportActions.setReleaseNoteData({
            jobInfo: {
              jobId: selectedRows[0].jobId,
              jobRequest: selectedRows[0].reqId,
            },
          }),
        );
        setPopUpContent(gridHeaders.REPORT_TYPE);

        setPopUpKey((prevState) => prevState + 1);
        modalRef.current?.showModal();
        break;
      }
      case 13: {
        setReleaseNoteVariant("additional");
        setShowCloseButton(true);
        setCurrentJobId(selectedRows[0].jobId);
        setCurrentJobCode(selectedRows[0].jobCode);
        setCurrentRequestId(selectedRows[0].reqId);
        setCurrentRequestCode(selectedRows[0].reqCode);
        dispatch(
          reportActions.setReleaseNoteData({
            jobInfo: {
              jobId: selectedRows[0].jobId,
              jobRequest: selectedRows[0].reqId,
            },
          }),
        );
        setPopUpContent(gridHeaders.REPORT_TYPE);
        setPopUpKey((prevState) => prevState + 1);
        modalRef.current?.showModal();
        break;
      }
      case 2: {
        let allSucceeded = true;

        for (let row of selectedRows) {
          try {
            await jobConfirmHandler(row, false);
          } catch (err) {
            allSucceeded = false;
          }
        }

        setUpdateNeeded(true);

        if (allSucceeded) {
          toast.success("Job confirmed successfully", {
            autoClose: 5000,
            containerId: "Main",
          });
        } else {
          toast.info("Error occurred while confirming job", {
            autoClose: 3000,
            containerId: "Main",
          });
        }
        break;
      }
      case 3: {
        setEvInvMode(mode.EV);
        setIsTemplateLoading(true);
        const templates = await getTemplates();
        setTemplates(templates);
        setIsTemplateLoading(false);
        openTemplateModal();
        break;
      }
      case 4: {
        setEvInvMode(mode.INV);
        setIsTemplateLoading(true);
        const templates = await getTemplates();
        setTemplates(templates);
        setIsTemplateLoading(false);
        openTemplateModal();
        break;
      }
      case 9: {
        const row = selectedRows[0];
        setCurrentJobId(row.jobId);
        setCurrentJobCode(row.jobCode);
        setCurrentRequestId(row.reqId);
        setCurrentRequestCode(row.reqCode);
        setPopUpContent(gridHeaders.REQUEST_COPY);
        setPopUpKey((prevState) => prevState + 1);
        openReqCopyModal();
        break;
      }
      case 10: {
        // const mappedData = [];
        const selectedJobIds =
          selectedRows?.length > 0
            ? selectedRows
                .filter((item) => item.latestFlag !== null) // First filter
                .map((item) => item.jobId) // Then map
            : [];
        downloadFlagLog(selectedJobIds);
        break;
      }
      case 11: {
        const selectedJobIds =
          selectedRows?.length > 0
            ? selectedRows?.map((item) => item.jobId)
            : [];
        handleVehicleAssignmentHistoryDownload(selectedJobIds);

        break;
      }
      case 12: {
        // const selectedJobIds =
        //   selectedRows?.length > 0
        //     ? selectedRows?.map((item) => item.jobId)
        //     : [];
        const selectedJobIds = [
          ...new Set(
            (selectedRows ?? [])
              .map((r) => r.jobId)
              .filter((id) => id !== null && id !== undefined),
          ),
        ];

        if (isExpired) {
          console.log("isExpired", true);
          handleShipmentStatusDownload(selectedJobIds);
        } else {
          console.log("isExpired", false);
          toast.success(
            `This data is cached and may be up to ${shipmentStatusInvalidateDuration ?? 10} minutes old. It will refresh automatically on the next search.`,
            {
              autoClose: 10000,
              containerId: "Main",
            },
          );
          handleShipmentStatusPerformancedReportDownload(selectedJobIds);
        }
        break;
      }
      case 15: {
        try {
          const selectedJobId = selectedRows[0].jobId;
          const idList = [selectedJobId];

          await retryKmActual(idList);
          await retryChargeableKM(idList);

          toast.success("KM recalculation triggered successfully!", {
            autoClose: 5000,
            containerId: "Main",
          });

          setUpdateNeeded(true);
        } catch (err) {
          toast.error("Failed to trigger KM recalculation.", {
            autoClose: 5000,
            containerId: "Main",
          });
        }
        break;
      }
      case 14: {
        const selectedJobIds =
          selectedRows?.length > 0
            ? selectedRows
                .filter((item) => !item.jobDocFreeze && item.hasKmActual) // First filter
                .map((item) => item.jobId) // Then map
            : [];

        if (selectedJobIds.length > 0) {
          freezeAndUnFreeze(selectedJobIds, true, 1);
        } else {
          toast.error("No jobs found to freeze doc upload", {
            autoClose: 5000,
            containerId: "Main",
          });
        }
        break;
      }
      case 16: {
        const selectedJobIds =
          selectedRows?.length > 0
            ? selectedRows
                .filter((item) => !item.jobTimeFreeze && item.hasKmActual) // First filter
                .map((item) => item.jobId) // Then map
            : [];
        console.log("selectedJobIds", selectedJobIds);
        if (selectedJobIds.length > 0) {
          freezeAndUnFreeze(selectedJobIds, true, 2);
        } else {
          toast.error("No jobs found to freeze time update", {
            autoClose: 5000,
            containerId: "Main",
          });
        }
        break;
      }
      case 24: {
        const selectedJobIds =
          selectedRows?.length > 0
            ? selectedRows
                .filter((item) => item.jobDocFreeze && item.hasKmActual) // First filter
                .map((item) => item.jobId) // Then map
            : [];
        if (selectedJobIds.length > 0) {
          freezeAndUnFreeze(selectedJobIds, false, 1);
        } else {
          toast.error("No jobs found to unfreeze doc upload", {
            autoClose: 5000,
            containerId: "Main",
          });
        }
        break;
      }
      case 25: {
        const selectedJobIds =
          selectedRows?.length > 0
            ? selectedRows
                .filter((item) => item.jobTimeFreeze && item.hasKmActual) // First filter
                .map((item) => item.jobId) // Then map
            : [];
        if (selectedJobIds.length > 0) {
          freezeAndUnFreeze(selectedJobIds, false, 2);
        } else {
          toast.error("No jobs found to unfreeze time update", {
            autoClose: 5000,
            containerId: "Main",
          });
        }
        break;
      }
      case 26: {
        setFocType(26);
        focRef.current?.showModal();
        break;
      }
      case 27: {
        setFocType(27);
        focRef.current?.showModal();
        break;
      }
      case 28: {
      setShowCloseButton(true);
      setCurrentJobId(selectedRows[0].jobId);
      setPopUpContent(gridHeaders.LIVE_TRACK_LINK);
      setPopUpKey((prevState) => prevState + 1);
      modalRef.current?.showModal();
      break;
      }

      default:
    }
  };

  const freezeAndUnFreeze = async (jobIds, freeze, type) => {
    try {
      freeze ? setIsFreezing(true) : setIsUnFreezing(true);
      const response = await jobFreezeAndUnFreeze({
        jobIds: jobIds,
        type: type,
        freeze: freeze,
      });

      if (response.status === 200) {
        toast.success(response.data, {
          autoClose: 5000,
          containerId: "Main",
        });
        setUpdateNeeded(true);
      }
    } catch (e) {
      toast.error(e.message, {
        autoClose: 5000,
        containerId: "Main",
      });
      console.error(e);
    } finally {
      freeze ? setIsFreezing(false) : setIsUnFreezing(false);
    }
    freeze ? setIsFreezing(false) : setIsUnFreezing(false);
  };

  const handleGridButtonClick = (data, gridFormHeader) => {
    if (!data?.jobCode) return;
    const currentRes = searchResults.find(
      (res) => res.jobCode === data?.jobCode,
    );
    console.log("currentRes", currentRes);
    if (!currentRes) return;
    setCurrentJobId(currentRes.jobId);
    setCurrentJobCode(currentRes.jobCode);
    setCurrentRequestId(currentRes.reqId);
    setCurrentRequestCode(currentRes.reqCode);
    setShowCloseButton(true);
    if (gridFormHeader === gridHeaders.SUPPLER_NEGO) {
      setCurrentCustomer(currentRes.reqCustomerId);
      setCurrentTransporter(currentRes.jobTransporterId);
      setCurrentStatus(currentRes.staId);
    }
    if (gridFormHeader === gridHeaders.CUSTOMER_NEGO) {
      setCurrentCustomer(currentRes.reqCustomerId);
      setCurrentStatus(currentRes.staId);
    }

    if (gridFormHeader === gridHeaders.VEHICLE_STATUS) {
      setTrakingEnabled(currentRes.staAnimation === 1);
      setCurrentVehicle(currentRes.jobVehicle);
      setCurrentDriver(currentRes.jobDriver);
      setLastDepartureTime(currentRes.lastDepartureTime);
      setFirstArrivalTime(currentRes.firstArrivalTime);
      setShowCloseButton(false);
    }

    if (gridFormHeader === gridHeaders.DED_FUEL) {
      setCurrentVehicle(currentRes.jobVehicle);
      setCurrentTransporter(currentRes.jobTransporterId);
    }
    setPopUpContent(gridFormHeader);
    setPopUpKey((prevState) => prevState + 1);
    modalRef.current?.showModal();
  };

  const updateToOldState = ({ jobCode, oldValue, key }) => {
    setSearchResults((prevState) =>
      prevState.map((res) => {
        if (res.jobCode === jobCode) {
          return {
            ...res,
            [key]: oldValue,
          };
        }
        return res;
      }),
    );
  };

  const isGreaterThanMaxCbm = async (jobId, value) => {
    return maxCbmByJobId(jobId).then((maxCbm) => maxCbm < value);
  };

  const cellEditingSubmitHandler = (e) => {
    const currentRes = searchResults.find(
      (res) => res.jobCode === e.data?.jobCode,
    );
    if (e.colDef.cellEditor) {
      switch (e.colDef.cellEditor) {
        case "agNumberCellEditor": {
          if (!e.valueChanged || e.value === e.oldValue) {
            updateToOldState({
              jobCode: e.data?.jobCode,
              key: e.colDef.field,
              oldValue: e.oldValue,
            });
            return;
          }
          break;
        }
        default:
      }
    } else {
      console.log("the e variable: ", e);
      if (
        !e.valueChanged ||
        (e.value && e.value.trim() === e.oldValue.trim())
      ) {
        updateToOldState({
          jobCode: e.data?.jobCode,
          key: e.colDef.field,
          oldValue: e.oldValue,
        });
        return;
      }
    }

    const newVal = e.value;

    console.log("fieldName", eventKeys.JOB_CBM);
    console.log("fieldName e", e.colDef.field);

    switch (e.colDef.field) {
      case eventKeys.KM_ACTUAL: {
        setUpdateState({
          currentRes,
          key: eventKeys.KM_ACTUAL,
          value: newVal,
          rowData: e.data,
          oldValue: e.oldValue,
          updateOnCancel: true,
          updateState: true,
        });
        setCurrentJobId(e.data?.jobId);
        setCurrentRequestId(e.data?.reqId);
        setCurrentCustomer(e.data?.reqCustomerId);
        setAlertOpen(true);
        break;
      }
      case eventKeys.JOB_AOD: {
        setUpdateState({
          currentRes,
          key: eventKeys.JOB_AOD,
          value: newVal,
          rowData: e.data,
          oldValue: e.oldValue,
          updateOnCancel: true,
          updateState: true,
        });
        setAlertOpen(true);
        break;
      }
      case eventKeys.Job_Call_Center_Remark: {
        setUpdateState({
          currentRes,
          key: eventKeys.Job_Call_Center_Remark,
          value: newVal,
          rowData: e.data,
          oldValue: e.oldValue,
          updateOnCancel: true,
          updateState: true,
        });
        setAlertOpen(true);
        break;
      }
      case eventKeys.JOB_OTD_REMARK: {
        setUpdateState({
          currentRes,
          key: eventKeys.JOB_OTD_REMARK,
          value: newVal,
          rowData: e.data,
          oldValue: e.oldValue,
          updateOnCancel: true,
          updateState: true,
        });
        setAlertOpen(true);
        break;
      }
      case eventKeys.JOB_ADDITIONAL_NO: {
        const isNumeric = /^\d*$/.test(newVal);
        if (!isNumeric) {
          toast.error("Only numbers are allowed.", {
            autoClose: 5000,
            containerId: "Main",
          });
          updateToOldState({
            jobCode: e.data?.jobCode,
            key: e.colDef.field,
            oldValue: e.oldValue,
          });
          return;
        }
        if (newVal.length > 15) {
          toast.error("Number cannot be more than 15 digits.", {
            autoClose: 5000,
            containerId: "Main",
          });
          updateToOldState({
            jobCode: e.data?.jobCode,
            key: e.colDef.field,
            oldValue: e.oldValue,
          });
          return;
        }
        setUpdateState({
          currentRes,
          key: eventKeys.JOB_ADDITIONAL_NO,
          value: newVal,
          rowData: e.data,
          oldValue: e.oldValue,
          updateOnCancel: true,
          updateState: true,
        });
        setAlertOpen(true);
        break;
      }
      case eventKeys.JOB_SEAL1: {
        setUpdateState({
          currentRes,
          key: eventKeys.JOB_SEAL1,
          value: newVal,
          rowData: e.data,
          oldValue: e.oldValue,
          updateOnCancel: true,
          updateState: true,
        });
        setAlertOpen(true);
        break;
      }
      case eventKeys.JOB_SEAL2: {
        setUpdateState({
          currentRes,
          key: eventKeys.JOB_SEAL2,
          value: newVal,
          rowData: e.data,
          oldValue: e.oldValue,
          updateOnCancel: true,
          updateState: true,
        });
        setAlertOpen(true);
        break;
      }
      case eventKeys.JOB_SEAL3: {
        setUpdateState({
          currentRes,
          key: eventKeys.JOB_SEAL3,
          value: newVal,
          rowData: e.data,
          oldValue: e.oldValue,
          updateOnCancel: true,
          updateState: true,
        });
        setAlertOpen(true);
        break;
      }
      case eventKeys.JOB_MOUX: {
        setUpdateState({
          currentRes,
          key: eventKeys.JOB_MOUX,
          value: newVal,
          rowData: e.data,
          oldValue: e.oldValue,
          updateOnCancel: true,
          updateState: true,
        });
        setAlertOpen(true);
        break;
      }
      case eventKeys.JOB_CARTON: {
        setUpdateState({
          currentRes,
          key: eventKeys.JOB_CARTON,
          value: newVal,
          rowData: e.data,
          oldValue: e.oldValue,
          updateOnCancel: true,
          updateState: true,
        });
        setAlertOpen(true);
        break;
      }
      case eventKeys.JOB_REF: {
        setUpdateState({
          currentRes,
          key: eventKeys.JOB_REF,
          value: newVal,
          rowData: e.data,
          oldValue: e.oldValue,
          updateOnCancel: true,
          updateState: true,
        });
        setAlertOpen(true);
        break;
      }
      case eventKeys.JOB_VEH_ASSIGN_REMARK: {
        setUpdateState({
          currentRes,
          key: eventKeys.JOB_VEH_ASSIGN_REMARK,
          value: newVal,
          rowData: e.data,
          oldValue: e.oldValue,
          updateOnCancel: true,
          updateState: true,
        });
        setAlertOpen(true);
        break;
      }
      case eventKeys.JOB_OPERATION_REMARK: {
        setUpdateState({
          currentRes,
          key: eventKeys.JOB_OPERATION_REMARK,
          value: newVal,
          rowData: e.data,
          oldValue: e.oldValue,
          updateOnCancel: true,
          updateState: true,
        });
        setAlertOpen(true);
        break;
      }
      case eventKeys.JOB_CUSTOMER_FEEDBACK: {
        setUpdateState({
          currentRes,
          key: eventKeys.JOB_CUSTOMER_FEEDBACK,
          value: newVal,
          rowData: e.data,
          oldValue: e.oldValue,
          updateOnCancel: true,
          updateState: true,
        });
        setAlertOpen(true);
        break;
      }
      case eventKeys.JOB_HELDUP_REMARK: {
        setUpdateState({
          currentRes,
          key: eventKeys.JOB_HELDUP_REMARK,
          value: newVal,
          rowData: e.data,
          oldValue: e.oldValue,
          updateOnCancel: true,
          updateState: true,
        });
        setAlertOpen(true);
        break;
      }
      case eventKeys.JOB_CBM: {
        isGreaterThanMaxCbm(e.data?.jobId, newVal).then((tooBig) => {
          if (!tooBig) {
            setUpdateState({
              currentRes,
              key: "jobCbm",
              value: newVal,
              rowData: e.data,
              oldValue: e.oldValue,
              updateOnCancel: true,
              updateState: true,
            });
            console.log("arrived");
            setAlertOpen(true);
          } else {
            toast.error("Job Cbm can't be greater than Max Cbm", {
              containerId: "Main",
              autoClose: 5000,
            });
          }
        });
        // const bool = isGreaterThanMaxCbm(e.data?.jobId, newVal);
        // console.log("Bool", bool);
        // if (!bool) {
        //   setUpdateState({
        //     currentRes,
        //     key: "jobCbm",
        //     value: newVal,
        //     rowData: e.data,
        //     oldValue: e.oldValue,
        //     updateOnCancel: true,
        //     updateState: true,
        //   });
        //   console.log("arrived");
        //   setAlertOpen(true);
        // } else {
        //   toast.error("Job Cbm can't be greater than Max Cbm", {
        //     containerId: "Main",
        //     autoClose: 5000,
        //   });
        // }
        break;
      }
      case eventKeys.REQ_PO_NO: {
        setUpdateState({
          currentRes,
          key: "jobPonumber",
          value: newVal,
          rowData: e.data,
          oldValue: e.oldValue,
          updateOnCancel: true,
          updateState: true,
        });
        setAlertOpen(true);
        break;
      }
      default:
    }
  };

  const cellDoubleClickHandler = (e) => {
    setShowCloseButton(true);

    const { authenticated, secondaryForm, field } = e.colDef;

    let permission = false;
    if (secondaryForm) {
      const { view } = getFormAuthority(auth, secondaryForm, false);
      permission = view;
    } else {
      const { authorized } = getFieldAuthority(
        auth,
        form,
        eventKeys.KM_ACTUAL,
        false,
        false,
      );
      permission = authorized;
    }

    if (authenticated && !permission) {
      return;
    }

    switch (field) {
      case eventKeys.KM_ACTUAL: {
        if (e.data?.kmMethodCount === null || e.data?.kmMethodCount <= 1) {
          return;
        }
        if (e.data?.hasKmActual) {
          setKmModeViewOnly(true);
          setKmActualAmount(e.data[eventKeys.KM_ACTUAL]);
        } else {
          setKmModeViewOnly(false);
          setKmActualAmount(null);
        }
        setCurrentJobId(e.data?.jobId);
        setCurrentRequestId(e.data?.reqId);
        setCurrentCustomer(e.data?.reqCustomerId);
        setPopUpContent(gridHeaders.KM_ACTUAL);
        break;
      }
      case eventKeys.STATUS_VALUE: {
        setCurrentStatus(e.data?.staId);
        setCurrentJobId(e.data?.jobId);
        setCurrentJobCode(e.data?.jobCode);
        setCurrentRequestId(e.data?.reqId);
        setCurrentRequestCode(e.data?.reqCode);
        setTimeFreeze(e.data?.jobTimeFreeze);
        setPopUpContent(gridHeaders.STATUS);
        break;
      }
      case eventKeys.VEHICLE: {
        setCurrentStaId(e.data?.staId);
        setCurrentJobId(e.data?.jobId);
        setCurrentJobCode(e.data?.jobCode);
        setCurrentRequestId(e.data?.reqId);
        setCurrentRequestCode(e.data?.reqCode);
        setCurrentVehicleType(e.data?.vhtType);
        setCurrentVehicleTypeId(e.data?.reqVehicleType);
        setCurrentContainerTypeId(e.data?.datareqContainerType);
        setPopUpContent(gridHeaders.VEHICLE_INFO);
        break;
      }
      case eventKeys.FLAG: {
        setCurrentJobId(e.data?.jobId);
        setCurrentJobCode(e.data?.jobCode);
        setCurrentRequestId(e.data?.reqId);
        setCurrentRequestCode(e.data?.reqCode);
        setPopUpContent(gridHeaders.FLAG);
        setCurrentStatus(e.data?.staId);
        break;
      }
      case eventKeys.REMARKS: {
        setCurrentJobId(e.data?.jobId);
        setCurrentJobCode(e.data?.jobCode);
        setCurrentRequestId(e.data?.reqId);
        setCurrentRequestCode(e.data?.reqCode);
        setCurrentVehicleType(e.data?.vhtType);
        setPopUpContent(gridHeaders.REMARKS);
        setCurrentStatus(e.data?.staId);
        break;
      }
      case eventKeys.REQUEST: {
        setRequestViewMode(
          userData.type !== 1 ||
            !requestEditModeEnabledListIds.includes(e.data?.staId),
        );
        setCurrentRequestId(e.data?.reqId);
        setCurrentJobId(e.data?.jobId);
        setCurrentRequestCode(e.data?.reqCode);
        setPopUpContent(null);
        setDrawerContent(gridHeaders.REQUEST);
        handleDrawerOpen();
        return;
      }
      case eventKeys.JOB: {
        setRequestViewMode(
          userData.type !== 1 ||
            !requestEditModeEnabledListIds.includes(e.data?.staId),
        );
        setCurrentRequestId(e.data?.reqId);
        setCurrentJobId(e.data?.jobId);
        setCurrentRequestCode(e.data?.reqCode);
        setPopUpContent(null);
        setDrawerContent(gridHeaders.REQUEST);
        handleDrawerOpen();
        return;
      }
      case eventKeys.LOCATION: {
        const data = e.data;
        setTimeUpdateViewMode(!tueList.includes(data?.staId));
        setCurrentCustomer(data?.reqCustomerId);
        setCurrentJobId(data?.jobId);
        setCurrentJobCode(data?.jobCode);
        setCurrentRequestId(data?.reqId);
        setCurrentRequestCode(data?.reqCode);
        setJobInfo({
          jobCode: data?.jobCode,
          reqCode: data?.reqCode,
          customer: data?.gReqCustomer,
          invoicingCustomer: data?.gReqInvoicingCustomer,
          plant: data?.gReqPlant,
          vehicleType: data?.vhtType,
          driver: data?.gDriverName,
          helper: data?.gHelperName,
          vehicle: data?.gJobVehicle,
          transporter: data?.gJobTransporter,
          cbm: data?.gReqCbm,
          statusId: data?.staId,
          status: data?.gStaName,
          statusColor: data?.staColor,
          bookingRequiredDate: data?.reqBookingRequiredDate
            ? formatFromJsonDate(data?.reqBookingRequiredDate)
            : "N/A",
          approvedDate: data?.gReqBookingApprovalDate,
          createdDate: data?.gCreatedDate,
          createdBy: data?.gCreatedBy,
          returnRemark: data?.returnRemarks,
        });
        setBookingRequiredDate(data?.reqBookingRequiredDate);
        setPopUpContent(null);
        setDrawerContent(gridHeaders.TIME_UPDATE);
        handleDrawerOpen();
        return;
      }

      default: {
        return;
      }
    }
    setPopUpKey((prevState) => prevState + 1);
    modalRef.current?.showModal();
  };

  return (
    <div>
      {isTemplateLoading ||
      isDownloading ||
      isSsrDownloading ||
      isFreezing ||
      isUnFreezing ? (
        <div className="blur-overlay">
          <LoadingSpinner
            message={
              isTemplateLoading
                ? `Loading Templates...`
                : isDownloading || isSsrDownloading
                  ? `Downloading...`
                  : isFreezing
                    ? `Freezing...`
                    : isUnFreezing
                      ? `UnFreezing...`
                      : `Loading...`
            }
          />
        </div>
      ) : (
        ""
      )}
      {/* {isDownloading && (
        <div className="blur-overlay">
          <LoadingSpinner message={`Downloading...`} />
        </div>
      )}
      {isSsrDownloading && (
        <div className="blur-overlay">
          <LoadingSpinner message={`Downloading...`} />
        </div>
      )} */}
      <AlertConfirmation
        onConfirm={async () => {
          try {
            setDropdownUpdateLoading(true);
            if (updateState.key === eventKeys.KM_ACTUAL) {
              await selectKmMethod({
                jobId: currentJobId,
                reqId: currentRequestId,
                customerId: currentCustomer,
                jkcId: null,
                kmValue: updateState.value,
                isManual: true,
              });
              setUpdateNeeded(true);
            } else {
              await update({
                currentRes: updateState.currentRes,
                key: updateState.key,
                value: updateState.value,
                rowData: updateState.rowData,
                updateState: updateState.updateState,
              });
              if (updateState.key === eventKeys.DOC_APPROVED) {
                setUpdateNeeded(true);
              }
            }
            setAlertOpen(false);
          } catch (e) {
            const message =
              e?.response?.data || "Error while removing update cargo in hand!";

            toast.error(message, {
              containerId: "Main",
              autoClose: 3000,
            });

            console.error(e);
          } finally {
            setDropdownUpdateLoading(false);
          }
        }}
        onCancel={() => {
          if (updateState.updateOnCancel) {
            updateToOldState({
              jobCode: updateState.rowData.jobCode,
              key: updateState.key,
              oldValue: updateState.oldValue,
            });
          }
          setAlertOpen(false);
        }}
        loadingMessage="Updating..."
        message="Are you sure you want to update?"
        isLoading={dropdownUpdateLoading}
        open={alertOpen}
      />
      <Modal ref={modalRef2} minHeight="60vh" minWidth="50vw">
        <input
          type="text"
          style={{ position: "absolute", opacity: 0 }}
          autoFocus
        />
        <div className="two fields">
          <div className="field">
            <Dropdown
              clearable={true}
              label="Select Template"
              options={templates}
              placeholder={isTemplateLoading ? "Loading..." : "Select Template"}
              style={{ height: "20px" }}
              value={currentTemplate}
              onChange={(e, data) => {
                setCurrentTemplate(data?.value);
              }}
              disabled={isTemplateLoading ? true : false}
            />
          </div>
          <div style={{ height: "20px", margin: "25px 0 0 0" }}>
            <Button
              label="Pick From Template"
              disabled={currentTemplate ? false : true}
              onClick={(e) => {
                pickFromTemplate(e);
              }}
            />
          </div>
        </div>
      </Modal>
      <Modal
        ref={modalRef}
        closeButton={
          popUpContent === gridHeaders.COLUMN_CONFIG ? false : showCloseButton
        }
        minWidth={
          popUpContent === gridHeaders.KM_ACTUAL
            ? "600px"
            : popUpContent === gridHeaders.COLUMN_CONFIG
              ? "400px"
              : "1000px"
        }
        onCloseCleanUp={() => {
          wsConnection.disconnect();
        }}
        clickOutSide={popUpContent !== gridHeaders.COLUMN_CONFIG}
      >
        {popUpComponent}
      </Modal>
      <Modal ref={focRef} closeButton={false} minWidth={"100px"}>
        {focType === 26 ? focRevContent : focType === 27 ? focCostContent : ""}
      </Modal>

      <div
        className="ui attached tabular"
        style={{
          display: "flex",
          justifyContent: "space-around",
        }}
      >
        <div className="ui tabular menu top attached">
          <Tab
            Icon={Info}
            onClick={() => {
              setTab(1);
            }}
            active={tab === 1}
            label="Job Monitoring"
          />
          {jobReportPermission && (
            <Tab
              Icon={Download}
              onClick={() => {
                setTab(2);
              }}
              active={tab === 2}
              label="Job Report"
            />
          )}
        </div>

        <div
          style={{
            display: tab === 1 ? "flex" : "none",
            justifyContent: "flex-end",
            marginTop: "0px",
          }}
          className="ui tabular menu top attached"
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1.5rem",
            }}
          >
            {jobMonitorTemplatePermission && (
              <Radio
                label="Job Monitor"
                name="templateGroup"
                value={template.JOB_MONITOR_TEMPLATE}
                checked={activeTemplate === template.JOB_MONITOR_TEMPLATE}
                onChange={(e, { value }) => {
                  setActiveTemplate(template.JOB_MONITOR_TEMPLATE);
                  localStorage.setItem(
                    ACTIVE_TEMPLATE,
                    String(template.JOB_MONITOR_TEMPLATE),
                  );
                  setSelectedHeaders(jobMonitorColumnList);
                  localStorage.setItem(
                    STORAGE_KEY,
                    JSON.stringify(jobMonitorColumnList),
                  );
                }}
              />
            )}

            {timeUpdateTemplatePermission && (
              <Radio
                label="Time Update"
                name="templateGroup"
                value={template.TIME_UPDATE_TEMPLATE}
                checked={activeTemplate === template.TIME_UPDATE_TEMPLATE}
                onChange={(e, { value }) => {
                  setActiveTemplate(template.TIME_UPDATE_TEMPLATE);
                  localStorage.setItem(
                    ACTIVE_TEMPLATE,
                    String(template.TIME_UPDATE_TEMPLATE),
                  );
                  setSelectedHeaders(timeUpdateColumnList);
                  localStorage.setItem(
                    STORAGE_KEY,
                    JSON.stringify(timeUpdateColumnList),
                  );
                }}
              />
            )}

            {chargePanelTemplatePermission && (
              <Radio
                label="Confirmation Panel"
                name="templateGroup"
                value={template.CHARGE_PANEL_TEMPLATE}
                checked={activeTemplate === template.CHARGE_PANEL_TEMPLATE}
                onChange={(e, { value }) => {
                  setActiveTemplate(template.CHARGE_PANEL_TEMPLATE);
                  localStorage.setItem(
                    ACTIVE_TEMPLATE,
                    String(template.CHARGE_PANEL_TEMPLATE),
                  );
                  setSelectedHeaders(chargePanelColumnList);
                  localStorage.setItem(
                    STORAGE_KEY,
                    JSON.stringify(chargePanelColumnList),
                  );
                }}
              />
            )}
          </div>
        </div>
      </div>
      <>
        <div
          className="ui bottom attached active tab segment"
          style={{ display: tab === 1 ? "block" : "none" }}
        >
          <div
            className="ui styled accordion"
            style={{ width: "100%", marginBottom: "10px" }}
          >
            <div
              className={`title ${activeAccordion === 1 ? "active" : ""}`}
              onClick={() => handleAccordionClick(1)}
            >
              <div>
                Search
                <Search size={14} style={{ marginLeft: "10px" }} />
                <i className="dropdown icon"></i>
              </div>
            </div>
            <div className={`content ${activeAccordion === 1 ? "active" : ""}`}>
              <JobMonitorSearch
                searchResHandler={(data) => {
                  handleSearch(data);
                  gridRef?.current?.evictPageCache();
                  setDefaultFilter("");
                }}
                resetHanler={() => {
                  handleReset();
                  gridRef?.current?.evictPageCache();
                  setDefaultFilter("");
                }}
              />
              <div className="ui clearing divider"></div>
            </div>
            <div
              className={`title ${activeAccordion === 2 ? "active" : ""}`}
              onClick={() => handleAccordionClick(2)}
            >
              <div>
                Advance Search
                <Search size={14} style={{ marginLeft: "10px" }} />
                <i className="dropdown icon"></i>
              </div>
            </div>
            <div className={`content ${activeAccordion === 2 ? "active" : ""}`}>
              <JobMonitorAdvancedSearch
                searchResHandler={(data) => {
                  handleSearch(data);
                  gridRef?.current?.evictPageCache();
                }}
                isLoading={isLoading}
                resetHanler={() => {
                  handleReset();
                  gridRef?.current?.evictPageCache();
                }}
              />
            </div>
          </div>
          {searchClicked && (
            <>
              {isSearchResultsPending || isLoading ? (
                <LoadingSpinner />
              ) : (
                <Grid
                  key={`aggrid:job-monitor_${activeTemplate}`}
                  persistCurrentPage={true}
                  fullRowEdit={false}
                  columnsMovable={false}
                  stopEditingWhenCellsLoseFocus={true}
                  defaultFilterText={defaultFilter}
                  multiRowSelection={true}
                  heading={
                    activeTemplate === template.JOB_MONITOR_TEMPLATE
                      ? "Job Monitoring"
                      : activeTemplate === template.TIME_UPDATE_TEMPLATE
                        ? "Time Update"
                        : "Confirmation Panel"
                  }
                  isLoading={isSearchResultsPending}
                  onCellEditingStopped={cellEditingSubmitHandler}
                  onCellDoubleClicked={cellDoubleClickHandler}
                  onSelectionChanged={handleSelectionChanged}
                  showActionMenu={showActionMenu}
                  actionMenuOptions={actionMenuOptions}
                  handleActionMenuChange={actionMenuHandler}
                  storageKey={`aggrid:job-monitor_${activeTemplate}`}
                  persistColumnConfig={true}
                  persistSortOnly={true}
                  showDownloadButton={selectedRows && selectedRows.length > 0}
                  downloadParam={JSON.stringify(currentSearchData)}
                  downloadAction="Job Monitor Excel Download"
                  showColumnConfigButton={true}
                  onColumnConfigClicked={openColumnConfig}
                  autoHeight={false}
                  rowHeight={50}
                  searchable={true}
                  config={gridConfig}
                  content={searchResults}
                  ref={gridRef}
                  columnVirtualisation={false}
                  reportName={
                    template.JOB_MONITOR_TEMPLATE === activeTemplate
                      ? "job_monitor_report.csv"
                      : template.TIME_UPDATE_TEMPLATE === activeTemplate
                        ? "Time_udate_report.csv"
                        : "confirmation_panel_report.csv"
                  }
                  vrScroll={true}
                  style={{
                    height: "600px",
                  }}
                />
              )}
            </>
          )}
        </div>
        {jobReportPermission && (
          <div
            className="ui bottom attached active tab segment"
            style={{ display: tab === 2 ? "block" : "none" }}
          >
            <JobReport />
          </div>
        )}

        <SideDrawer
          open={isDrawerOpen}
          handleDrawerClose={handleDrawerClose}
          head={headerComponent}
          body={drawerComponent}
          style={{ width: "90vw" }}
        />
      </>
    </div>
  );
}

export default JobMonitor;

const styles = `
  .blur-overlay {
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
    z-index: 9999; /* Ensure it's on top of everything */
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .blurred {
    filter: blur(5px); /* Blur effect on content */
    pointer-events: none; /* Prevent interaction with blurred content */
  }
`;

document.head.insertAdjacentHTML("beforeend", `<style>${styles}</style>`);
